import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { SchoolworkOverview } from "./schoolwork-overview.js";

import {
  assignmentCandidateListSchema,
  parentCandidateDetailsSnapshotSchema,
  type ParentCandidateDetailsSnapshot,
  type SaveCandidateDetailsCommand,
  assignmentCandidateImportResultSchema,
  dailyQueueCloseoutCommandResultSchema,
  dailyQueueCloseoutPreviewSchema,
  dailyQueueCommandResultSchema,
  focusCommandResultSchema,
  householdSnapshotSchema,
  parentOverrideCommandResultSchema,
  recoveryCommandResultSchema,
  schoolworkCommandResultSchema,
  policyDeliveryStatusSchema,
  snapshotRequiredNotificationSchema,
  startActiveStepCommandResultSchema,
  studentPairingCodeIssuedSchema,
  studentEventNotificationSchema,
  studentSnapshotNotificationSchema,
  surfaceAcknowledgementResultSchema,
  type DailyQueueDraftInput,
  type AssignmentCandidateImportResult,
  type CanvasAssignmentCandidateInput,
  type DailyQueueCloseoutCommandResult,
  type DailyQueueCloseoutPreview,
  type DailyQueueSnapshot,
  type FocusBlockSnapshot,
  type HouseholdSnapshot,
  type ParentOverrideCommandResult,
  type ParentOverrideStateSnapshot,
  type Role,
  type SchoolworkCommandResult,
  type StudentRecoverySnapshot,
  type SchoolworkProgressSnapshot,
  type PolicyDeliveryStatus,
  type SurfaceKind,
  type StudentWorkStateSnapshot,
  type StudentPairingCodeIssued,
} from "@vsta/contracts";
import { activeStepWorkday, type StudentWorkState } from "@vsta/domain";

import {
  FocusBlockOutcomeUnconfirmedError,
  FocusBlockPanel,
  type FocusBlockConnectivity,
  type FocusBlockUiCommand,
} from "./focus-block.js";
import {
  BreakRecoveryPanel,
  type RecoveryUiCommand,
} from "./break-recovery.js";
import {
  AssignmentProgress,
  StudentOutcomeControls,
  UnderstandingChecks,
  type SchoolworkUiCommand,
  type SubmissionEvidenceUiCommand,
} from "./work-outcomes.js";
import {
  QueuePlanner,
  QueuePlannerSubmissionValidationError,
  type QueuePlannerInput,
} from "./queue-planner.js";
import {
  applyStudentEventNotification,
  applyStudentSnapshotAnchor,
  createStudentSyncProjection,
} from "./synchronization.js";
import {
  ParentOverrideDecisionPanel,
  StudentSkipPanel,
  type OverrideDecisionUiCommand,
  type OverrideCancellationUiCommand,
  type SafeAlternative,
  type SkipUiCommand,
} from "./parent-override.js";
import {
  DayCloseoutHistory,
  DayCloseoutPanel,
  type DayCloseoutUiCommand,
} from "./day-closeout.js";
import {
  applyTheme,
  readStoredTheme,
  storeTheme,
  themeOptions,
  themeStorageKey,
  type ThemeId,
} from "./theme.js";

export type SessionClient = {
  load: () => Promise<HouseholdSnapshot>;
  subscribeToDailyQueueChanges?: (
    studentId: string,
    onChange: () => void,
    onConnectivityChange?: (connectivity: FocusBlockConnectivity) => void,
    surfaceKind?: SurfaceKind,
    initialSequence?: number,
    onSnapshot?: (snapshot: HouseholdSnapshot) => void,
  ) => () => void;
  acknowledgeSurface?: (
    surfaceKind: SurfaceKind,
    snapshot: HouseholdSnapshot,
  ) => Promise<void>;
  loadPolicyDeliveryStatuses?: (
    studentId: string,
  ) => Promise<PolicyDeliveryStatus[]>;
  parentLogin?: (credentials: {
    email: string;
    password: string;
  }) => Promise<void>;
  studentPair?: (code: string) => Promise<void>;
  issueStudentPairingCode?: (
    studentId: string,
    expectedRevision: number,
  ) => Promise<StudentPairingCodeIssued>;
  importAssignmentCandidates?: (
    studentId: string,
    candidates: CanvasAssignmentCandidateInput[],
  ) => Promise<AssignmentCandidateImportResult>;
  loadAssignmentCandidates?: (
    studentId: string,
  ) => Promise<CanvasAssignmentCandidateInput[]>;
  saveCandidateDetails?: (
    studentId: string,
    command: SaveCandidateDetailsCommand,
  ) => Promise<ParentCandidateDetailsSnapshot>;
  loadStudentSchoolwork?: () => Promise<CanvasAssignmentCandidateInput[]>;
  createDailyQueue?: (
    studentId: string,
    draft: DailyQueueDraftInput,
  ) => Promise<DailyQueueSnapshot>;
  updateDailyQueue?: (
    studentId: string,
    queueId: string,
    expectedRevision: number,
    draft: DailyQueueDraftInput,
  ) => Promise<DailyQueueSnapshot>;
  markDailyQueueReady?: (
    studentId: string,
    queueId: string,
    expectedRevision: number,
  ) => Promise<DailyQueueSnapshot>;
  activateDailyQueue?: (
    studentId: string,
    queueId: string,
    expectedRevision: number,
  ) => Promise<DailyQueueSnapshot>;
  reorderDailyQueue?: (
    queueId: string,
    expectedRevision: number,
    orderedQueueItemIds: string[],
  ) => Promise<DailyQueueSnapshot>;
  startActiveStep?: (
    studentId: string,
    queueId: string,
    queueItemId: string,
    expectedRevision: number,
    intent: "start" | "switch",
  ) => Promise<StudentWorkStateSnapshot>;
  startImportedActivity?: (command: {
    requestId: string;
    sourceItemKey: string;
    expectedRevision: number;
    intent: "start" | "switch";
  }) => Promise<HouseholdSnapshot>;
  runFocusBlockCommand?: (
    studentId: string,
    focusBlockId: string,
    expectedRevision: number,
    command: FocusBlockUiCommand,
  ) => Promise<FocusBlockSnapshot>;
  runRecoveryCommand?: (
    studentId: string,
    expectedRevision: number,
    command: RecoveryUiCommand,
  ) => Promise<StudentRecoverySnapshot>;
  runSchoolworkCommand?: (
    studentId: string,
    command: SchoolworkUiCommand,
  ) => Promise<SchoolworkProgressSnapshot>;
  recordSubmissionEvidence?: (
    studentId: string,
    command: SubmissionEvidenceUiCommand,
  ) => Promise<SchoolworkProgressSnapshot>;
  runProtectedSkip?: (
    studentId: string,
    command: SkipUiCommand,
  ) => Promise<SchoolworkCommandResult>;
  decideParentOverride?: (
    studentId: string,
    command: OverrideDecisionUiCommand,
  ) => Promise<ParentOverrideCommandResult>;
  cancelParentOverride?: (
    studentId: string,
    command: OverrideCancellationUiCommand,
  ) => Promise<ParentOverrideCommandResult>;
  loadDayCloseoutPreview?: (
    studentId: string,
    role: "parent" | "student",
  ) => Promise<DailyQueueCloseoutPreview>;
  runDayCloseout?: (
    studentId: string,
    role: "parent" | "student",
    command: DayCloseoutUiCommand,
  ) => Promise<DailyQueueCloseoutCommandResult>;
};

export type ResourceLauncher = (
  url: string,
  target: "_blank",
  features: "noopener,noreferrer",
) => boolean;

type AppProps = {
  initialPath?: string;
  sessionClient?: SessionClient;
  resourceLauncher?: ResourceLauncher;
};
type LoadState =
  | { status: "loading" }
  | { status: "loaded"; snapshot: HouseholdSnapshot }
  | {
      status: "error";
      kind: "authentication-required" | "unavailable";
      message: string;
    };
type PolicyDeliveryLoadState =
  | { status: "loading" }
  | { status: "loaded"; surfaces: PolicyDeliveryStatus[] }
  | { status: "error" };

export class AuthenticationRequiredError extends Error {
  override readonly name = "AuthenticationRequiredError";
}

export class DailyQueueConflictError extends Error {
  override readonly name = "DailyQueueConflictError";
  constructor(
    message: string,
    readonly snapshot: DailyQueueSnapshot,
  ) {
    super(message);
  }
}

export class ActiveStepConflictError extends Error {
  override readonly name = "ActiveStepConflictError";
  constructor(
    message: string,
    readonly snapshot: StudentWorkStateSnapshot,
    readonly changedBy: Role,
    readonly changedAt: string,
    readonly authoritativeWinner: {
      stepId: string;
      queueItemId: string;
    } | null,
  ) {
    super(message);
  }
}

export class FocusBlockConflictError extends Error {
  override readonly name = "FocusBlockConflictError";
  constructor(
    message: string,
    readonly snapshot: FocusBlockSnapshot | null,
    readonly changedBy: Role,
    readonly changedAt: string,
  ) {
    super(message);
  }
}

export class RecoveryConflictError extends Error {
  override readonly name = "RecoveryConflictError";
  constructor(
    message: string,
    readonly snapshot: StudentRecoverySnapshot,
    readonly changedBy: Role,
    readonly changedAt: string,
  ) {
    super(message);
  }
}

export class SchoolworkConflictError extends Error {
  override readonly name = "SchoolworkConflictError";
  constructor(
    message: string,
    readonly snapshot: SchoolworkProgressSnapshot,
    readonly changedBy: Role,
    readonly changedAt: string,
  ) {
    super(message);
  }
}

async function dailyQueueCommand(
  path: string,
  method: "POST" | "PUT",
  body: Record<string, unknown>,
): Promise<DailyQueueSnapshot> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ requestId: crypto.randomUUID(), ...body }),
    });
  } catch {
    throw new Error(
      "The Daily Queue service is unavailable. Your entries are still here; try again when the connection returns.",
    );
  }

  let result;
  try {
    result = dailyQueueCommandResultSchema.parse(await response.json());
  } catch {
    throw new Error(
      "The Daily Queue service returned an unexpected response. Your entries are still here; try again.",
    );
  }
  if (result.status === "accepted") return result.snapshot;
  if (result.error.code === "VALIDATION_FAILED") {
    throw new QueuePlannerSubmissionValidationError(result.error.issues);
  }
  if (result.error.code === "REVISION_CONFLICT") {
    throw new DailyQueueConflictError(
      `This Daily Queue changed in another ${result.error.changedBy} session. Your entries were not changed. Review them carefully; saving again will replace the newer Draft at revision ${result.error.currentRevision}.`,
      result.error.snapshot,
    );
  }
  throw new Error(`${result.error.message} Your entries are still here.`);
}

async function startActiveStepCommand(input: {
  expectedRevision: number;
  queueId: string;
  queueItemId: string;
  intent: "start" | "switch";
}): Promise<StudentWorkStateSnapshot> {
  let response: Response;
  try {
    response = await fetch("/api/student/active-step", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ requestId: crypto.randomUUID(), ...input }),
    });
  } catch {
    throw new Error(
      "The Active Step service is unavailable. Nothing changed; try again when the connection returns.",
    );
  }

  let result;
  try {
    result = startActiveStepCommandResultSchema.parse(await response.json());
  } catch {
    throw new Error(
      "The Active Step service returned an unexpected response. Nothing changed; try again.",
    );
  }
  if (result.status === "accepted") return result.snapshot;
  if (result.error.code === "REVISION_CONFLICT") {
    throw new ActiveStepConflictError(
      "The Active Step changed in another session.",
      result.error.snapshot,
      result.error.changedBy,
      result.error.changedAt,
      result.error.authoritativeWinner,
    );
  }
  if (result.error.code === "VALIDATION_FAILED") {
    throw new Error(
      result.error.issues[0]?.message ??
        "Check the selected Queue Item and try again.",
    );
  }
  throw new Error(result.error.message);
}

async function protectedSkipCommand(
  command: SkipUiCommand,
): Promise<SchoolworkCommandResult> {
  let response: Response;
  try {
    response = await fetch("/api/student/work-outcome", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        requestId: crypto.randomUUID(),
        intent: "skip",
        ...command,
      }),
    });
  } catch {
    throw new Error(
      "The Skip request could not reach the service. Nothing was changed.",
    );
  }
  try {
    return schoolworkCommandResultSchema.parse(await response.json());
  } catch {
    throw new Error(
      "The Skip service returned an unexpected response. Nothing was silently accepted.",
    );
  }
}

async function parentOverrideCommand(
  path: string,
  command: OverrideDecisionUiCommand | OverrideCancellationUiCommand,
): Promise<ParentOverrideCommandResult> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ requestId: crypto.randomUUID(), ...command }),
    });
  } catch {
    throw new Error(
      "The Parent Override service is unavailable. No decision was changed.",
    );
  }
  try {
    return parentOverrideCommandResultSchema.parse(await response.json());
  } catch {
    throw new Error(
      "The Parent Override service returned an unexpected response. No decision was silently accepted.",
    );
  }
}

async function runFocusBlockCommand(input: {
  focusBlockId: string;
  expectedRevision: number;
  command: FocusBlockUiCommand;
}): Promise<FocusBlockSnapshot> {
  let response: Response;
  try {
    response = await fetch("/api/student/focus-block", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        requestId: crypto.randomUUID(),
        focusBlockId: input.focusBlockId,
        expectedRevision: input.expectedRevision,
        ...input.command,
      }),
    });
  } catch {
    throw new FocusBlockOutcomeUnconfirmedError(
      "The Focus Block service is unavailable. We could not confirm whether the Focus Block changed. Reconnect or refresh to verify the authoritative state.",
    );
  }

  let result;
  try {
    result = focusCommandResultSchema.parse(await response.json());
  } catch {
    throw new FocusBlockOutcomeUnconfirmedError(
      "The Focus Block service returned an unexpected response. We could not confirm whether the Focus Block changed. Reconnect or refresh to verify the authoritative state.",
    );
  }
  if (result.status === "accepted") return result.snapshot;
  if (result.error.code === "REVISION_CONFLICT") {
    throw new FocusBlockConflictError(
      "This Focus Block changed in another session.",
      result.error.snapshot,
      result.error.changedBy,
      result.error.changedAt,
    );
  }
  if (result.error.code === "VALIDATION_FAILED") {
    throw new Error(
      result.error.issues[0]?.message ??
        "Check this timer action and try again.",
    );
  }
  throw new Error(result.error.message);
}

async function runRecoveryCommand(input: {
  expectedRevision: number;
  command: RecoveryUiCommand;
}): Promise<StudentRecoverySnapshot> {
  const command = input.command;
  const identifiers =
    command.intent === "start-break"
      ? { breakId: crypto.randomUUID() }
      : command.intent === "im-stuck"
        ? { transitionId: crypto.randomUUID() }
        : command.intent === "park-thought"
          ? { entryId: crypto.randomUUID() }
          : {};
  let response: Response;
  try {
    response = await fetch("/api/student/recovery", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        requestId: crypto.randomUUID(),
        expectedRevision: input.expectedRevision,
        ...command,
        ...identifiers,
      }),
    });
  } catch {
    throw new Error(
      "Break and recovery service is unavailable. Your typed Parking Lot text remains here; reconnect and try again.",
    );
  }
  let result;
  try {
    result = recoveryCommandResultSchema.parse(await response.json());
  } catch {
    throw new Error(
      "Break and recovery service returned an unexpected response. Refresh to verify the authoritative state.",
    );
  }
  if (result.status === "accepted") return result.snapshot;
  if (result.error.code === "REVISION_CONFLICT") {
    throw new RecoveryConflictError(
      "Break or recovery state changed in another session.",
      result.error.snapshot,
      result.error.changedBy,
      result.error.changedAt,
    );
  }
  if (result.error.code === "VALIDATION_FAILED") {
    throw new Error(
      result.error.issues[0]?.message ?? "Check this action and try again.",
    );
  }
  throw new Error(result.error.message);
}

async function schoolworkCommand(
  path: string,
  command:
    | SchoolworkUiCommand
    | (SubmissionEvidenceUiCommand & {
        intent: "record-submission-evidence";
      }),
): Promise<SchoolworkProgressSnapshot> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ requestId: crypto.randomUUID(), ...command }),
    });
  } catch {
    throw new Error(
      "The schoolwork outcome service is unavailable. Nothing was confirmed; reconnect and verify the current state.",
    );
  }
  let result;
  try {
    result = schoolworkCommandResultSchema.parse(await response.json());
  } catch {
    throw new Error(
      "The schoolwork outcome service returned an unexpected response. Nothing was silently accepted.",
    );
  }
  if (result.status === "accepted") return result.snapshot;
  if (result.status === "override-required") {
    throw new Error(
      "This action needs parent approval. Review the Parent Override panel.",
    );
  }
  if (result.error.code === "REVISION_CONFLICT") {
    throw new SchoolworkConflictError(
      "Schoolwork changed in another session.",
      result.error.snapshot,
      result.error.changedBy,
      result.error.changedAt,
    );
  }
  throw new Error(result.error.message);
}

export const browserResourceLauncher: ResourceLauncher = (
  url,
  target,
  features,
) => {
  const opened = window.open(url, target, features);
  if (opened !== null) opened.opener = null;
  return opened !== null;
};

export const browserSessionClient: SessionClient = {
  startImportedActivity: async (command) => {
    let response: Response;
    try {
      response = await fetch("/api/student/imported-activity/start", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(command),
      });
    } catch {
      throw new Error(
        "The start confirmation did not arrive. Check your timer or retry this activity; retrying will not start a second timer.",
      );
    }
    const result = startActiveStepCommandResultSchema.safeParse(
      await response.json(),
    );
    if (!result.success)
      throw new Error(
        "The start response could not be confirmed. Refresh your day to check the timer.",
      );
    if (result.data.status === "rejected") {
      const error = result.data.error;
      throw new Error(
        "message" in error
          ? error.message
          : "Your day changed. Refresh before starting this activity.",
      );
    }
    try {
      return await browserSessionClient.load();
    } catch {
      throw new Error(
        "Start was accepted, but the timer display could not refresh. Retry this activity to reload it without starting a second timer.",
      );
    }
  },
  load: async () => {
    const response = await fetch("/api/session", {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationRequiredError("Sign in to see this household.");
      }
      throw new Error("Today’s plan could not be loaded.");
    }
    return householdSnapshotSchema.parse(await response.json());
  },
  parentLogin: async (credentials) => {
    const response = await fetch("/api/auth/parent", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(credentials),
    });
    if (!response.ok) throw new Error("The email or password did not match.");
  },
  studentPair: async (code) => {
    const response = await fetch("/api/auth/student-pairing", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!response.ok)
      throw new Error("That pairing code is invalid or expired.");
  },
  issueStudentPairingCode: async (studentId, expectedRevision) => {
    const response = await fetch(
      `/api/parent/students/${encodeURIComponent(studentId)}/pairing-code`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          requestId: crypto.randomUUID(),
          expectedRevision,
        }),
      },
    );
    const body = (await response.json()) as { message?: unknown };
    if (!response.ok) {
      throw new Error(
        typeof body.message === "string"
          ? body.message
          : "A Student Pairing Code could not be generated.",
      );
    }
    return studentPairingCodeIssuedSchema.parse(body);
  },
  importAssignmentCandidates: async (studentId, candidates) => {
    const response = await fetch(
      `/api/parent/students/${encodeURIComponent(studentId)}/assignment-candidates/import`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({ requestId: crypto.randomUUID(), candidates }),
      },
    );
    const body = (await response.json()) as { message?: unknown };
    if (!response.ok) {
      throw new Error(
        typeof body.message === "string"
          ? body.message
          : "The imported assignments could not be saved.",
      );
    }
    return assignmentCandidateImportResultSchema.parse(body);
  },
  saveCandidateDetails: async (studentId, command) => {
    const response = await fetch(
      `/api/parent/students/${encodeURIComponent(studentId)}/assignment-candidates/details`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify(command),
      },
    );
    const body = (await response.json()) as { message?: unknown };
    if (!response.ok)
      throw new Error(
        typeof body.message === "string"
          ? body.message
          : "Parent details could not be saved. Your entries are still here.",
      );
    return parentCandidateDetailsSnapshotSchema.parse(body);
  },
  loadAssignmentCandidates: async (studentId) => {
    const response = await fetch(
      `/api/parent/students/${encodeURIComponent(studentId)}/assignment-candidates`,
      {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      },
    );
    if (!response.ok) {
      throw new Error("Previously imported Canvas activities are unavailable.");
    }
    return assignmentCandidateListSchema.parse(await response.json())
      .candidates;
  },
  loadStudentSchoolwork: async () => {
    const response = await fetch("/api/student/assignment-candidates", {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (!response.ok) throw new Error("Saved schoolwork is unavailable.");
    return assignmentCandidateListSchema.parse(await response.json())
      .candidates;
  },
  createDailyQueue: async (studentId, draft) =>
    dailyQueueCommand(
      `/api/parent/students/${studentId}/daily-queues`,
      "POST",
      { expectedRevision: 0, draft },
    ),
  updateDailyQueue: async (studentId, queueId, expectedRevision, draft) =>
    dailyQueueCommand(
      `/api/parent/students/${studentId}/daily-queues/${queueId}`,
      "PUT",
      { expectedRevision, draft },
    ),
  markDailyQueueReady: async (studentId, queueId, expectedRevision) =>
    dailyQueueCommand(
      `/api/parent/students/${studentId}/daily-queues/${queueId}/ready`,
      "POST",
      { expectedRevision },
    ),
  activateDailyQueue: async (studentId, queueId, expectedRevision) =>
    dailyQueueCommand(
      `/api/parent/students/${studentId}/daily-queues/${queueId}/activate`,
      "POST",
      { expectedRevision },
    ),
  reorderDailyQueue: async (queueId, expectedRevision, orderedQueueItemIds) =>
    dailyQueueCommand("/api/student/daily-queue/reorder", "POST", {
      intent: "reorder",
      queueId,
      expectedRevision,
      orderedQueueItemIds,
    }),
  startActiveStep: async (
    _studentId,
    queueId,
    queueItemId,
    expectedRevision,
    intent,
  ) =>
    startActiveStepCommand({
      expectedRevision,
      queueId,
      queueItemId,
      intent,
    }),
  runFocusBlockCommand: async (
    _studentId,
    focusBlockId,
    expectedRevision,
    command,
  ) => runFocusBlockCommand({ focusBlockId, expectedRevision, command }),
  runRecoveryCommand: async (_studentId, expectedRevision, command) =>
    runRecoveryCommand({ expectedRevision, command }),
  runSchoolworkCommand: async (_studentId, command) =>
    schoolworkCommand("/api/student/work-outcome", command),
  recordSubmissionEvidence: async (studentId, command) =>
    schoolworkCommand(
      `/api/parent/students/${encodeURIComponent(studentId)}/submission-evidence`,
      { ...command, intent: "record-submission-evidence" },
    ),
  runProtectedSkip: async (_studentId, command) =>
    protectedSkipCommand(command),
  decideParentOverride: async (studentId, command) =>
    parentOverrideCommand(
      `/api/parent/students/${encodeURIComponent(studentId)}/overrides/${encodeURIComponent(command.overrideId)}/decision`,
      command,
    ),
  cancelParentOverride: async (_studentId, command) =>
    parentOverrideCommand(
      `/api/student/overrides/${encodeURIComponent(command.overrideId)}/cancel`,
      command,
    ),
  loadDayCloseoutPreview: async (studentId, role) => {
    const path =
      role === "parent"
        ? `/api/parent/students/${encodeURIComponent(studentId)}/day-closeout`
        : "/api/student/day-closeout";
    const response = await fetch(path, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(
        "The current end-of-day review could not be loaded. Nothing changed.",
      );
    }
    return dailyQueueCloseoutPreviewSchema.parse(await response.json());
  },
  runDayCloseout: async (studentId, role, command) => {
    const path =
      role === "parent"
        ? `/api/parent/students/${encodeURIComponent(studentId)}/day-closeout`
        : "/api/student/day-closeout";
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        requestId: crypto.randomUUID(),
        intent: "close-day",
        ...command,
      }),
    });
    return dailyQueueCloseoutCommandResultSchema.parse(await response.json());
  },
  acknowledgeSurface: async (surfaceKind, snapshot) => {
    const response = await fetch("/api/sync/acknowledgements", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        requestId: crypto.randomUUID(),
        surfaceKind,
        eventSequence: snapshot.student.eventSequence,
        aggregateRevision: snapshot.student.eventSequence,
        policyRevision: snapshot.student.policyRevision,
      }),
    });
    const result = surfaceAcknowledgementResultSchema.safeParse(
      await response.json(),
    );
    if (!response.ok || !result.success) {
      throw new Error(
        "The latest synchronized state could not be acknowledged.",
      );
    }
  },
  loadPolicyDeliveryStatuses: async (studentId) => {
    const response = await fetch(
      `/api/parent/students/${encodeURIComponent(studentId)}/sync-status`,
      { credentials: "same-origin", headers: { accept: "application/json" } },
    );
    if (!response.ok)
      throw new Error("Surface delivery status is unavailable.");
    const body = (await response.json()) as { surfaces?: unknown[] };
    return (body.surfaces ?? []).map((surface) =>
      policyDeliveryStatusSchema.parse(surface),
    );
  },
  subscribeToDailyQueueChanges: (
    studentId,
    onChange,
    onConnectivityChange,
    surfaceKind = "student-console",
    initialSequence = 0,
    onSnapshot,
  ) => {
    if (typeof EventSource === "undefined") {
      onConnectivityChange?.(
        typeof navigator !== "undefined" && navigator.onLine === false
          ? "offline"
          : "reconnecting",
      );
      return () => undefined;
    }
    let projection = createStudentSyncProjection(initialSequence);
    const parseMessage = (rawEvent: Event): unknown => {
      try {
        return JSON.parse((rawEvent as MessageEvent<string>).data) as unknown;
      } catch {
        return null;
      }
    };
    const events = new EventSource(
      `/api/events?studentId=${encodeURIComponent(studentId)}&surfaceKind=${surfaceKind}`,
      { withCredentials: true },
    );
    events.addEventListener("StudentSnapshot", (rawEvent) => {
      const parsed = studentSnapshotNotificationSchema.safeParse(
        parseMessage(rawEvent),
      );
      if (!parsed.success) {
        onConnectivityChange?.("conflict");
        onChange();
        return;
      }
      projection = applyStudentSnapshotAnchor(projection, parsed.data.sequence);
      onConnectivityChange?.("pending");
      if (onSnapshot === undefined) onChange();
      else onSnapshot(parsed.data.snapshot);
    });
    events.addEventListener("StudentEvent", (rawEvent) => {
      const parsed = studentEventNotificationSchema.safeParse(
        parseMessage(rawEvent),
      );
      if (!parsed.success) {
        onConnectivityChange?.("conflict");
        onChange();
        return;
      }
      if (parsed.data.sequence <= projection.lastSequence) return;
      projection = applyStudentEventNotification(projection, parsed.data);
      onConnectivityChange?.(
        projection.status === "snapshot-required" ? "reconnecting" : "pending",
      );
      onChange();
    });
    events.addEventListener("SnapshotRequired", (rawEvent) => {
      const parsed = snapshotRequiredNotificationSchema.safeParse(
        parseMessage(rawEvent),
      );
      onConnectivityChange?.(parsed.success ? "reconnecting" : "conflict");
      onChange();
    });
    events.addEventListener("open", () =>
      onConnectivityChange?.("reconnecting"),
    );
    events.addEventListener("error", () =>
      onConnectivityChange?.(
        typeof navigator !== "undefined" && navigator.onLine === false
          ? "offline"
          : "reconnecting",
      ),
    );
    return () => events.close();
  },
};

function SignIn({
  sessionClient,
  onAuthenticated,
}: {
  sessionClient: SessionClient;
  onAuthenticated: (role: Role) => void;
}) {
  const [submitting, setSubmitting] = useState<null | Role>(null);
  const [error, setError] = useState<string | null>(null);

  async function submitParent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSubmitting("parent");
    setError(null);
    try {
      const login = sessionClient.parentLogin;
      if (login === undefined)
        throw new Error("Parent sign-in is unavailable.");
      await login({
        email: String(data.get("email") ?? ""),
        password: String(data.get("password") ?? ""),
      });
      onAuthenticated("parent");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Sign-in failed.");
      setSubmitting(null);
    }
  }

  async function submitStudent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSubmitting("student");
    setError(null);
    try {
      const pair = sessionClient.studentPair;
      if (pair === undefined)
        throw new Error("Student pairing is unavailable.");
      await pair(String(data.get("pairingCode") ?? ""));
      onAuthenticated("student");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Pairing failed.");
      setSubmitting(null);
    }
  }

  return (
    <main className="sign-in" id="main-content">
      <div className="sign-in-intro">
        <p className="eyebrow">Daylight</p>
        <h1>Sign in for today’s plan</h1>
        <p>
          Parents prepare the plan. Students use a short-lived code generated by
          their parent.
        </p>
      </div>
      {error === null ? null : <p role="alert">{error}</p>}
      <div className="sign-in-options">
        <form onSubmit={(event) => void submitParent(event)}>
          <h2>Parent sign in</h2>
          <label htmlFor="parent-email">Parent email</label>
          <input
            id="parent-email"
            name="email"
            type="email"
            autoComplete="username"
            required
          />
          <label htmlFor="parent-password">Parent password</label>
          <input
            id="parent-password"
            name="password"
            type="password"
            autoComplete="current-password"
            minLength={12}
            required
          />
          <button type="submit" disabled={submitting !== null}>
            {submitting === "parent" ? "Signing in…" : "Sign in as parent"}
          </button>
        </form>
        <form onSubmit={(event) => void submitStudent(event)}>
          <h2>Student pairing</h2>
          <label htmlFor="student-pairing-code">
            6-digit Student Pairing Code
          </label>
          <p id="student-pairing-help" className="form-help">
            Ask your parent for a new code. It expires in one minute.
          </p>
          <input
            id="student-pairing-code"
            name="pairingCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            minLength={6}
            maxLength={6}
            placeholder="000000"
            aria-describedby="student-pairing-help"
            required
          />
          <button type="submit" disabled={submitting !== null}>
            {submitting === "student" ? "Pairing…" : "Open student workspace"}
          </button>
        </form>
      </div>
    </main>
  );
}

function expectedRole(path: string): Role {
  return path === "/parent" ? "parent" : "student";
}

const accountabilityModeLabel: Record<
  HouseholdSnapshot["student"]["accountabilityMode"],
  string
> = { coach: "Coach", escalate: "Escalate", enforce: "Enforce" };

const supportLevelLabel: Record<
  HouseholdSnapshot["student"]["supportLevel"],
  string
> = {
  high: "High support",
  standard: "Standard support",
  light: "Light support",
  independent: "Independent support",
};

const recoveryBarrierLabel = {
  "unclear-directions": "Unclear directions",
  "step-too-large": "Step felt too large",
  "technical-issue": "Technical issue",
  distraction: "Distraction",
  "fatigue-unwell": "Tired or not feeling well",
  "waiting-for-help": "Waiting for help",
  other: "Something else",
} as const;

const recoveryActionLabel = {
  "reread-directions": "Reread directions and name the first action",
  "work-one-small-part": "Work one smaller part",
  "retry-resource": "Retry the Resource",
  "restart-for-five": "Restart with five minutes",
  "take-five-minute-break": "Take a five-minute Break",
  "request-help": "Request help",
  "continue-active-step": "Continue the Active Step",
} as const;

function connectivitySummary(connectivity: FocusBlockConnectivity) {
  if (connectivity === "offline") {
    return "Offline · showing last confirmed state";
  }
  if (connectivity === "reconnecting") {
    return "Reconnecting · showing last confirmed state";
  }
  if (connectivity === "pending") {
    return "Pending · confirming this surface";
  }
  if (connectivity === "conflict") {
    return "Conflict · refresh before another consequential change";
  }
  return "Connected · authoritative state acknowledged";
}

function SurfaceHeader({
  snapshot,
  connectivity,
  studentSurface,
  theme,
  onThemeChange,
}: {
  snapshot: HouseholdSnapshot;
  connectivity: FocusBlockConnectivity;
  studentSurface?: "console" | "workspace";
  theme: ThemeId;
  onThemeChange: (theme: ThemeId) => void;
}) {
  return (
    <header className="surface-header">
      <div className="surface-branding">
        <a
          className="brand"
          href={snapshot.role === "parent" ? "/parent" : "/student"}
        >
          Daylight
        </a>
        {snapshot.role === "student" && studentSurface !== undefined ? (
          <nav aria-label="Student surfaces" className="surface-navigation">
            <a
              aria-current={studentSurface === "console" ? "page" : undefined}
              href="/student"
            >
              Student Console
            </a>
            <a
              aria-current={studentSurface === "workspace" ? "page" : undefined}
              href="/workspace"
            >
              Managed Workspace
            </a>
          </nav>
        ) : null}
      </div>
      <div className="policy-summary" aria-label="Current work policy">
        <span className="policy-chip">
          {accountabilityModeLabel[snapshot.student.accountabilityMode]}
        </span>
        <span>{supportLevelLabel[snapshot.student.supportLevel]}</span>
      </div>
      <label className="theme-selector">
        <span>Theme</span>
        <select
          aria-label="Theme"
          value={theme}
          onChange={(event) =>
            onThemeChange(event.currentTarget.value as ThemeId)
          }
        >
          {themeOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <p
        className={`surface-connectivity connectivity-${connectivity}`}
        aria-live="polite"
      >
        {connectivitySummary(connectivity)}
      </p>
    </header>
  );
}

function dateTimeParts(date: Date, timezone: string) {
  const values = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );
  return {
    year: values.year ?? 0,
    month: values.month ?? 0,
    day: values.day ?? 0,
    hour: values.hour ?? 0,
    minute: values.minute ?? 0,
  };
}

function localDateTimeInput(instant: string, timezone: string) {
  const part = dateTimeParts(new Date(instant), timezone);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${part.year}-${pad(part.month)}-${pad(part.day)}T${pad(part.hour)}:${pad(part.minute)}`;
}

function zonedDateTimeInstant(localDateTime: string, timezone: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localDateTime);
  if (match === null) return localDateTime;
  const desired = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
  const desiredUtc = Date.UTC(
    desired.year,
    desired.month - 1,
    desired.day,
    desired.hour,
    desired.minute,
  );
  let instant = desiredUtc;
  for (let pass = 0; pass < 2; pass += 1) {
    const actual = dateTimeParts(new Date(instant), timezone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
    );
    instant -= actualAsUtc - desiredUtc;
  }
  return new Date(instant).toISOString();
}

function plannerDraft(input: QueuePlannerInput): DailyQueueDraftInput {
  return {
    ...input,
    assignments: input.assignments.map((assignment) => ({
      ...assignment,
      ...(assignment.dueAt === undefined
        ? {}
        : { dueAt: zonedDateTimeInstant(assignment.dueAt, input.timezone) }),
    })),
  };
}

function queueToPlannerInput(queue: DailyQueueSnapshot): QueuePlannerInput {
  return {
    localDate: queue.localDate,
    startTime: queue.startTime,
    endTime: queue.endTime,
    timezone: queue.timezone,
    unavailableWindows: queue.unavailableWindows,
    deferredStepIds: queue.deferredStepIds,
    courses: queue.courses,
    units: queue.units ?? [],
    assignments: queue.assignments.map(({ dueAt, unitId, ...assignment }) => ({
      ...assignment,
      ...(unitId === undefined ? {} : { unitId }),
      ...(dueAt === undefined
        ? {}
        : {
            dueAt: localDateTimeInput(dueAt, queue.timezone),
          }),
    })),
    resources: queue.resources.map(({ url, description, ...resource }) => ({
      ...resource,
      ...(url === undefined ? {} : { url }),
      ...(description === undefined ? {} : { description }),
    })),
    topics: queue.topics,
    steps: queue.steps,
    queueItems: queue.queueItems.map((item) => ({
      ...item,
      planning: {
        mustFinish: item.planning.mustFinish,
        ...(item.planning.studentReorderEligible === undefined
          ? {}
          : {
              studentReorderEligible: item.planning.studentReorderEligible,
            }),
        minMinutes: item.planning.minMinutes,
        maxMinutes: item.planning.maxMinutes,
      },
    })),
  };
}

function DayCloseoutRegion({
  snapshot,
  role,
  sessionClient,
  connectivity,
  overrides,
  onSafeAlternative,
}: {
  snapshot: HouseholdSnapshot;
  role: "parent" | "student";
  sessionClient: SessionClient;
  connectivity: FocusBlockConnectivity;
  overrides: ParentOverrideStateSnapshot | null;
  onSafeAlternative: (alternative: SafeAlternative) => Promise<void | string>;
}) {
  const completed = snapshot.dayCloseout ?? null;
  const activeQueue =
    snapshot.dailyQueue?.status === "active" ? snapshot.dailyQueue : null;
  const currentQueueCloseout =
    activeQueue !== null && completed?.dailyQueueId === activeQueue.id
      ? completed
      : null;
  const [preview, setPreview] = useState<DailyQueueCloseoutPreview | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  async function refresh() {
    const loadPreview = sessionClient.loadDayCloseoutPreview;
    if (activeQueue === null || currentQueueCloseout !== null) return;
    if (loadPreview === undefined) return;
    setLoading(true);
    setLoadError(null);
    try {
      setPreview(await loadPreview(snapshot.student.id, role));
    } catch (reason) {
      setLoadError(
        reason instanceof Error
          ? reason.message
          : "The current end-of-day review could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (activeQueue === null || currentQueueCloseout !== null) {
      setPreview(null);
      setLoadError(null);
      return;
    }
    void refresh();
  }, [
    activeQueue?.id,
    activeQueue?.revision,
    currentQueueCloseout?.id,
    role,
    snapshot.student.id,
  ]);

  if (currentQueueCloseout !== null) {
    return <DayCloseoutHistory closeout={currentQueueCloseout} />;
  }
  if (activeQueue === null) {
    return completed === null ? null : (
      <DayCloseoutHistory closeout={completed} />
    );
  }
  if (sessionClient.loadDayCloseoutPreview === undefined) return null;
  if (preview !== null) {
    return (
      <DayCloseoutPanel
        role={role}
        connectivity={connectivity}
        preview={preview}
        overrides={
          overrides ?? {
            revision: 0,
            changedBy: role,
            changedAt: new Date(0).toISOString(),
            overrides: [],
          }
        }
        onClose={async (command) => {
          const close = sessionClient.runDayCloseout;
          if (close === undefined) {
            throw new Error("End-of-day closeout is temporarily unavailable.");
          }
          return close(snapshot.student.id, role, command);
        }}
        onRefresh={refresh}
        onSafeAlternative={onSafeAlternative}
      />
    );
  }
  return (
    <section className="day-closeout-loading" aria-label="End-of-day review">
      {loadError === null ? (
        <p role="status">
          {loading
            ? "Loading the factual end-of-day review…"
            : "Preparing the factual end-of-day review…"}
        </p>
      ) : (
        <div role="alert">
          <p>{loadError}</p>
          <button
            type="button"
            className="secondary-button"
            disabled={loading || connectivity !== "connected"}
            onClick={() => void refresh()}
          >
            Retry end-of-day review
          </button>
        </div>
      )}
    </section>
  );
}

function formatPairingCountdown(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function StudentPairingAccess({
  student,
  sessionClient,
  connectivity,
}: {
  student: HouseholdSnapshot["student"];
  sessionClient: SessionClient;
  connectivity: FocusBlockConnectivity;
}) {
  const [accessRevision, setAccessRevision] = useState(student.accessRevision);
  const [issuedCode, setIssuedCode] = useState<StudentPairingCodeIssued | null>(
    null,
  );
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<string | null>(null);

  useEffect(() => {
    setAccessRevision((current) => Math.max(current, student.accessRevision));
  }, [student.accessRevision]);

  useEffect(() => {
    if (issuedCode === null) return;
    let expirationAnnounced = false;
    const updateCountdown = () => {
      const remaining = Math.max(
        0,
        Math.ceil(
          (new Date(issuedCode.expiresAt).getTime() - Date.now()) / 1_000,
        ),
      );
      setSecondsRemaining(remaining);
      if (remaining === 0 && !expirationAnnounced) {
        expirationAnnounced = true;
        setAnnouncement(
          "The Student Pairing Code expired. Generate a new code to continue.",
        );
      }
    };
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 250);
    return () => window.clearInterval(timer);
  }, [issuedCode]);

  const expired = issuedCode !== null && secondsRemaining === 0;

  async function generateCode() {
    const issue = sessionClient.issueStudentPairingCode;
    setGenerating(true);
    setError(null);
    setAnnouncement(null);
    try {
      if (issue === undefined) {
        throw new Error("Student pairing is temporarily unavailable.");
      }
      if (connectivity !== "connected") {
        throw new Error("Reconnect before generating a Student Pairing Code.");
      }
      const result = await issue(student.id, accessRevision);
      setAccessRevision(result.acceptedRevision);
      setSecondsRemaining(result.expiresInSeconds);
      setIssuedCode(result);
      setAnnouncement(
        `A new Student Pairing Code was generated for ${student.displayName}. It expires in one minute.`,
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "A Student Pairing Code could not be generated.",
      );
    } finally {
      setGenerating(false);
    }
  }

  async function copyCode() {
    if (issuedCode === null || expired) return;
    try {
      if (navigator.clipboard === undefined) {
        throw new Error("Clipboard access is unavailable.");
      }
      await navigator.clipboard.writeText(issuedCode.code);
      setAnnouncement("Student Pairing Code copied.");
    } catch {
      setAnnouncement(
        "Copy is unavailable. Select the six digits and copy them manually.",
      );
    }
  }

  return (
    <section
      className="student-access-panel"
      aria-labelledby="student-access-title"
    >
      <div className="student-access-copy">
        <p className="status-label">Student access</p>
        <h2 id="student-access-title">Pair {student.displayName}'s device</h2>
        <p>
          Generate a six-digit code, then enter it on the Student pairing form
          within one minute.
        </p>
        <p id="pairing-code-consequence" className="student-access-note">
          Generating a new code signs out existing Student sessions.
        </p>
      </div>
      <div className="student-access-action">
        {issuedCode === null ? null : expired ? (
          <p className="pairing-code-expired" role="status">
            Code expired
          </p>
        ) : (
          <div
            className="pairing-code-status"
            role="group"
            aria-label="Student Pairing Code"
          >
            <span className="pairing-code">{issuedCode.code}</span>
            <span className="pairing-countdown">
              Expires in {formatPairingCountdown(secondsRemaining)}
            </span>
          </div>
        )}
        <div className="student-access-buttons">
          <button
            type="button"
            aria-describedby="pairing-code-consequence"
            disabled={generating || connectivity !== "connected"}
            onClick={() => void generateCode()}
          >
            {generating
              ? "Generating codeâ€¦"
              : issuedCode === null
                ? "Generate 6-digit code"
                : "Generate new code"}
          </button>
          {issuedCode === null || expired ? null : (
            <button
              type="button"
              className="secondary-button"
              onClick={() => void copyCode()}
            >
              Copy pairing code
            </button>
          )}
        </div>
        {error === null ? null : <p role="alert">{error}</p>}
        {announcement === null ? null : (
          <p className="student-access-announcement" aria-live="polite">
            {announcement}
          </p>
        )}
      </div>
    </section>
  );
}

function ParentDashboard({
  snapshot,
  sessionClient,
  connectivity,
  policyDeliveryState,
  onRetryPolicyDelivery,
  onConnectivityConflict,
  theme,
  onThemeChange,
}: {
  snapshot: HouseholdSnapshot;
  sessionClient: SessionClient;
  connectivity: FocusBlockConnectivity;
  policyDeliveryState: PolicyDeliveryLoadState;
  onRetryPolicyDelivery: () => void;
  onConnectivityConflict: () => void;
  theme: ThemeId;
  onThemeChange: (theme: ThemeId) => void;
}) {
  const [showPlanner, setShowPlanner] = useState(
    snapshot.dailyQueue?.status === "draft",
  );
  const [schoolworkVersion, setSchoolworkVersion] = useState(0);
  const loadSavedCandidates = useCallback(async () => {
    const load = sessionClient.loadAssignmentCandidates;
    if (load === undefined) return [];
    return load(snapshot.student.id);
  }, [sessionClient.loadAssignmentCandidates, snapshot.student.id]);
  const [queue, setQueue] = useState(snapshot.dailyQueue);
  const [schoolworkProgress, setSchoolworkProgress] = useState(
    snapshot.schoolworkProgress ?? null,
  );
  const [parentOverrides, setParentOverrides] = useState(
    snapshot.parentOverrides ?? null,
  );
  const [panelFailure, setPanelFailure] = useState<string | null>(null);
  const [schoolworkConflict, setSchoolworkConflict] = useState<string | null>(
    null,
  );
  const [draftConflict, setDraftConflict] = useState<{
    local: QueuePlannerInput;
    remote: DailyQueueSnapshot;
  } | null>(null);
  const [publishedReconciliation, setPublishedReconciliation] = useState<{
    local: QueuePlannerInput;
    remote: DailyQueueSnapshot;
  } | null>(null);
  const [plannerVersion, setPlannerVersion] = useState(0);
  const [conflictChoiceMessage, setConflictChoiceMessage] = useState<
    string | null
  >(null);
  const [activating, setActivating] = useState(false);
  const [justActivated, setJustActivated] = useState(false);
  const [focusStatusAfterAcknowledgment, setFocusStatusAfterAcknowledgment] =
    useState(false);
  const queueRef = useRef(snapshot.dailyQueue);
  const latestPlannerRef = useRef<QueuePlannerInput | null>(
    snapshot.dailyQueue?.status === "draft"
      ? queueToPlannerInput(snapshot.dailyQueue)
      : null,
  );
  const plannerRegion = useRef<HTMLDivElement>(null);
  const reconciliationHeading = useRef<HTMLHeadingElement>(null);
  const statusHeading = useRef<HTMLHeadingElement>(null);
  const reconciliationCopy = useRef<HTMLTextAreaElement>(null);
  const schoolworkConflictRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setSchoolworkProgress(snapshot.schoolworkProgress ?? null);
  }, [snapshot.schoolworkProgress]);

  useEffect(() => {
    setParentOverrides(snapshot.parentOverrides ?? null);
  }, [snapshot.parentOverrides]);

  useEffect(() => {
    if (schoolworkConflict !== null) schoolworkConflictRef.current?.focus();
  }, [schoolworkConflict]);

  async function addSubmissionEvidence(command: SubmissionEvidenceUiCommand) {
    const record = sessionClient.recordSubmissionEvidence;
    if (record === undefined) {
      throw new Error("Submission Evidence is temporarily unavailable.");
    }
    if (connectivity !== "connected") {
      throw new Error(
        "Reconnect and confirm the current Assignment status before adding Submission Evidence.",
      );
    }
    try {
      const accepted = await record(snapshot.student.id, command);
      setSchoolworkConflict(null);
      setSchoolworkProgress(accepted);
      return accepted;
    } catch (error) {
      if (error instanceof SchoolworkConflictError) {
        setSchoolworkProgress(error.snapshot);
        const actor = error.changedBy === "parent" ? "parent" : "student";
        const message = `Another ${actor} session changed this Assignment. The newer server status is shown; Submission Evidence was not added. Your verification entry is preserved. Review the status and try again if evidence is still needed.`;
        setSchoolworkConflict(message);
        throw new Error(message, { cause: error });
      }
      throw error;
    }
  }

  async function decideParentOverride(command: OverrideDecisionUiCommand) {
    const decide = sessionClient.decideParentOverride;
    if (decide === undefined) {
      throw new Error("Parent Override decisions are temporarily unavailable.");
    }
    if (connectivity !== "connected") {
      throw new Error(
        "Reconnect and review the latest request before making this decision.",
      );
    }
    const result = await decide(snapshot.student.id, command);
    if (result.status === "accepted") {
      setParentOverrides(result.snapshot);
      return;
    }
    if (result.error.snapshot !== undefined) {
      setParentOverrides(result.error.snapshot);
    }
    throw new Error(result.error.message);
  }

  function rememberQueue(next: DailyQueueSnapshot | null) {
    queueRef.current = next;
    setQueue(next);
    return next;
  }

  function reconcilePublishedQueue(
    local: QueuePlannerInput,
    remote: DailyQueueSnapshot,
  ) {
    rememberQueue(remote);
    latestPlannerRef.current = local;
    setDraftConflict(null);
    setConflictChoiceMessage(null);
    setPublishedReconciliation({ local, remote });
    setShowPlanner(false);
  }

  async function runCommand(command: () => Promise<DailyQueueSnapshot>) {
    try {
      const accepted = rememberQueue(await command());
      if (accepted === null) {
        throw new Error("The Daily Queue command returned no snapshot.");
      }
      return accepted;
    } catch (reason) {
      if (reason instanceof DailyQueueConflictError) {
        rememberQueue(reason.snapshot);
        onConnectivityConflict();
      }
      throw reason;
    }
  }

  async function saveDraft(input: QueuePlannerInput) {
    if (connectivity !== "connected") {
      throw new Error(
        "Live state is not confirmed. Your entries remain here; reconnect before saving this Daily Queue.",
      );
    }
    latestPlannerRef.current = input;
    if (draftConflict !== null) {
      throw new Error(
        "Choose which Draft to keep in the conflict comparison before saving again.",
      );
    }
    const current = queueRef.current;
    try {
      if (current === null) {
        const create = sessionClient.createDailyQueue;
        if (create === undefined)
          throw new Error("Daily Queue planning is temporarily unavailable.");
        await runCommand(() =>
          create(snapshot.student.id, plannerDraft(input)),
        );
        return;
      }
      if (current.status !== "draft") {
        throw new Error(
          `This Daily Queue is ${current.status} and cannot be edited as a Draft.`,
        );
      }
      const update = sessionClient.updateDailyQueue;
      if (update === undefined)
        throw new Error("Daily Queue editing is temporarily unavailable.");
      await runCommand(() =>
        update(
          snapshot.student.id,
          current.id,
          current.revision,
          plannerDraft(input),
        ),
      );
    } catch (reason) {
      if (reason instanceof DailyQueueConflictError) {
        if (reason.snapshot.status === "draft") {
          setDraftConflict({ local: input, remote: reason.snapshot });
          setConflictChoiceMessage(null);
        } else {
          reconcilePublishedQueue(input, reason.snapshot);
        }
      }
      throw reason;
    }
  }

  async function markReady() {
    if (connectivity !== "connected") {
      throw new Error(
        "Live state is not confirmed. Reconnect before marking this Daily Queue Ready.",
      );
    }
    const current = queueRef.current;
    const ready = sessionClient.markDailyQueueReady;
    if (current === null || ready === undefined) {
      throw new Error("Save the Daily Queue Draft before marking it Ready.");
    }
    await runCommand(() =>
      ready(snapshot.student.id, current.id, current.revision),
    );
  }

  async function activate() {
    if (connectivity !== "connected") {
      throw new Error(
        "Live state is not confirmed. Reconnect before activating this Daily Queue.",
      );
    }
    const current = queueRef.current;
    const activateQueue = sessionClient.activateDailyQueue;
    if (current === null || activateQueue === undefined) {
      throw new Error("Mark the Daily Queue Ready before activating it.");
    }
    await runCommand(() =>
      activateQueue(snapshot.student.id, current.id, current.revision),
    );
  }

  useEffect(() => {
    if (!showPlanner) return;
    const heading = plannerRegion.current?.querySelector("h2");
    if (heading instanceof HTMLElement) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }, [showPlanner]);

  useEffect(() => {
    const remote = snapshot.dailyQueue;
    const current = queueRef.current;
    if (
      remote !== null &&
      current !== null &&
      remote.id === current.id &&
      (remote.revision < current.revision ||
        (remote.revision === current.revision &&
          remote.status === "draft" &&
          current.status !== "draft"))
    ) {
      return;
    }
    if (
      remote !== null &&
      current !== null &&
      remote.id === current.id &&
      remote.revision === current.revision &&
      remote.status === current.status
    ) {
      rememberQueue(remote);
      return;
    }
    const local = latestPlannerRef.current;
    if (
      showPlanner &&
      remote !== null &&
      remote.status !== "draft" &&
      local !== null
    ) {
      reconcilePublishedQueue(local, remote);
      return;
    }
    if (
      showPlanner &&
      remote?.status === "draft" &&
      local !== null &&
      JSON.stringify(local) !== JSON.stringify(queueToPlannerInput(remote))
    ) {
      rememberQueue(remote);
      setDraftConflict({ local, remote });
      setConflictChoiceMessage(null);
      return;
    }
    rememberQueue(remote);
  }, [showPlanner, snapshot.dailyQueue]);

  useEffect(() => {
    if (justActivated) statusHeading.current?.focus();
  }, [justActivated]);

  useEffect(() => {
    if (publishedReconciliation !== null) {
      reconciliationHeading.current?.focus();
    }
  }, [publishedReconciliation]);

  useEffect(() => {
    if (focusStatusAfterAcknowledgment && publishedReconciliation === null) {
      statusHeading.current?.focus();
      setFocusStatusAfterAcknowledgment(false);
    }
  }, [focusStatusAfterAcknowledgment, publishedReconciliation]);

  const remoteConflictDraft =
    draftConflict === null ? null : queueToPlannerInput(draftConflict.remote);
  const reconciliationText =
    publishedReconciliation === null
      ? ""
      : JSON.stringify(publishedReconciliation.local, null, 2);
  const policyDeliveryStatuses =
    policyDeliveryState.status === "loaded" ? policyDeliveryState.surfaces : [];

  return (
    <div className="app-shell">
      <SurfaceHeader
        snapshot={snapshot}
        connectivity={connectivity}
        theme={theme}
        onThemeChange={onThemeChange}
      />
      <main id="main-content" className="surface-main">
        <p className="eyebrow">Parent Dashboard</p>
        <h1>{queue === null ? "Student schoolwork" : "Today’s plan"}</h1>
        <p className="lead">
          {queue === null
            ? `${snapshot.student.displayName} can start imported activities without extra setup. Timed work appears here when they begin.`
            : `${snapshot.student.displayName}’s Daily Queue is ${queue.status}.`}
        </p>
        {panelFailure === null ? null : <p role="alert">{panelFailure}</p>}
        {sessionClient.loadAssignmentCandidates === undefined ? null : (
          <SchoolworkOverview
            key={schoolworkVersion}
            loadCandidates={loadSavedCandidates}
            {...(sessionClient.saveCandidateDetails === undefined
              ? {}
              : {
                  onSaveDetails: (command: SaveCandidateDetailsCommand) =>
                    sessionClient.saveCandidateDetails!(
                      snapshot.student.id,
                      command,
                    ),
                })}
            timezone={snapshot.student.timezone}
            onReview={() => {
              setShowPlanner(true);
              requestAnimationFrame(() => {
                const tab =
                  plannerRegion.current?.querySelector<HTMLButtonElement>(
                    '[role="tab"]',
                  );
                tab?.click();
                tab?.focus();
              });
            }}
          />
        )}
        {queue === null && !showPlanner ? (
          <section className="empty-panel" aria-labelledby="empty-plan-title">
            <div>
              <h2 id="empty-plan-title">Optional: customize focused work</h2>
              <p>
                Imported activities already appear on the Student Console. Use a
                Daily Queue if you want to customize Steps, work times or
                completion rules. Students can start timers directly from
                imported activities. This extra planning is optional.
              </p>
            </div>
            <button type="button" onClick={() => setShowPlanner(true)}>
              Build today’s Daily Queue
            </button>
          </section>
        ) : null}
        {queue?.status === "ready" && !showPlanner ? (
          <section className="queue-status-panel" aria-labelledby="ready-title">
            <div>
              <p className="status-label">Ready</p>
              <h2 id="ready-title" ref={statusHeading} tabIndex={-1}>
                Review complete
              </h2>
              <p>Activate this Daily Queue when the student can begin.</p>
            </div>
            <button
              type="button"
              disabled={activating || connectivity !== "connected"}
              onClick={() => {
                setPanelFailure(null);
                setActivating(true);
                void activate()
                  .then(() => setJustActivated(true))
                  .catch((reason: unknown) =>
                    setPanelFailure(
                      reason instanceof Error
                        ? reason.message
                        : "The Daily Queue could not be activated.",
                    ),
                  )
                  .finally(() => setActivating(false));
              }}
            >
              {activating ? "Activating…" : "Activate Daily Queue"}
            </button>
          </section>
        ) : null}
        {queue?.status === "active" && !showPlanner ? (
          <section
            className="queue-status-panel"
            aria-labelledby="active-title"
          >
            <div>
              <p className="status-label">Active</p>
              <h2 id="active-title" ref={statusHeading} tabIndex={-1}>
                The Daily Queue is available
              </h2>
              <p>
                The Student Console and Managed Workspace can now show the plan.
              </p>
              {justActivated ? (
                <p role="status">Daily Queue activated for the student.</p>
              ) : null}
            </div>
          </section>
        ) : null}
        {showPlanner ? (
          <div className="planner-panel" ref={plannerRegion}>
            <QueuePlanner
              key={plannerVersion}
              defaultTimezone={snapshot.student.timezone}
              {...(queue === null ? {} : { initialStatus: queue.status })}
              {...(queue === null
                ? {}
                : { initialValue: queueToPlannerInput(queue) })}
              onSaveDraft={saveDraft}
              onPlanChange={(input) => {
                latestPlannerRef.current = input;
              }}
              onMarkReady={async () => markReady()}
              onActivate={async () => activate()}
              onImportCandidates={async (candidates) => {
                const importCandidates =
                  sessionClient.importAssignmentCandidates;
                if (importCandidates === undefined) {
                  throw new Error(
                    "Schedule import is temporarily unavailable.",
                  );
                }
                const result = await importCandidates(
                  snapshot.student.id,
                  candidates,
                );
                setSchoolworkVersion((version) => version + 1);
                return result;
              }}
              onLoadCandidates={loadSavedCandidates}
              disabled={connectivity !== "connected"}
            />
          </div>
        ) : null}
        <StudentPairingAccess
          student={snapshot.student}
          sessionClient={sessionClient}
          connectivity={connectivity}
        />
        {snapshot.studentRecovery?.latestRecovery === null ||
        snapshot.studentRecovery === null ||
        snapshot.studentRecovery === undefined ? null : (
          <section
            className="parent-recovery-summary"
            aria-labelledby="parent-recovery-title"
          >
            <p className="status-label">Latest Recovery Check-in</p>
            <h2 id="parent-recovery-title">
              {
                recoveryBarrierLabel[
                  snapshot.studentRecovery.latestRecovery.barrier
                ]
              }
            </h2>
            <p>
              <strong>Next action:</strong>{" "}
              {
                recoveryActionLabel[
                  snapshot.studentRecovery.latestRecovery.action
                ]
              }
            </p>
          </section>
        )}
        {policyDeliveryStatuses.length > 0 ? null : (
          <section
            className="policy-delivery-panel"
            aria-labelledby="policy-delivery-title"
          >
            <p className="status-label">Policy delivery</p>
            <h2 id="policy-delivery-title">Student surface status</h2>
            <ul>
              <li>
                <strong>Student Console · Unknown</strong>
                <span>
                  {policyDeliveryState.status === "loading"
                    ? "Checking the latest acknowledgment…"
                    : "No acknowledgment yet. Retry to check this surface."}
                </span>
              </li>
              <li>
                <strong>Managed Workspace · Unknown</strong>
                <span>
                  {policyDeliveryState.status === "loading"
                    ? "Checking the latest acknowledgment…"
                    : "No acknowledgment yet. Retry to check this surface."}
                </span>
              </li>
            </ul>
            {policyDeliveryState.status === "loading" ? (
              <p role="status">Checking policy delivery status…</p>
            ) : policyDeliveryState.status === "error" ? (
              <div>
                <p role="alert">
                  Policy delivery status could not be checked. The last policy
                  is unchanged, but receipt is unknown.
                </p>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={onRetryPolicyDelivery}
                >
                  Retry policy status
                </button>
              </div>
            ) : (
              <button
                className="secondary-button"
                type="button"
                onClick={onRetryPolicyDelivery}
              >
                Retry policy status
              </button>
            )}
          </section>
        )}
        {policyDeliveryStatuses.length === 0 ? null : (
          <section
            className="policy-delivery-panel"
            aria-labelledby="policy-delivery-title"
          >
            <p className="status-label">Policy delivery</p>
            <h2 id="policy-delivery-title">Student surface status</h2>
            <ul>
              {policyDeliveryStatuses.map((surface) => {
                const surfaceLabel =
                  surface.surfaceKind === "student-console"
                    ? "Student Console"
                    : "Managed Workspace";
                const statusLabel =
                  surface.status.charAt(0).toUpperCase() +
                  surface.status.slice(1);
                return (
                  <li key={surface.surfaceKind}>
                    <strong>
                      {surfaceLabel} · {statusLabel}
                    </strong>
                    <span>
                      {surface.status === "connected"
                        ? `Policy revision ${surface.currentPolicyRevision} is acknowledged on this surface.`
                        : surface.status === "pending"
                          ? `Policy revision ${surface.currentPolicyRevision} is waiting for this connected surface to acknowledge it.`
                          : `This surface is offline. It will receive policy revision ${surface.currentPolicyRevision} after reconnecting.`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {schoolworkConflict === null ? null : (
          <section
            className="published-reconciliation"
            role="alert"
            aria-labelledby="schoolwork-conflict-title"
            tabIndex={-1}
            ref={schoolworkConflictRef}
          >
            <h2 id="schoolwork-conflict-title">Submission Evidence conflict</h2>
            <p>{schoolworkConflict}</p>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setSchoolworkConflict(null)}
            >
              Dismiss Submission Evidence conflict
            </button>
          </section>
        )}
        {schoolworkProgress === null ? null : (
          <AssignmentProgress
            role="parent"
            connectivity={connectivity}
            progress={schoolworkProgress}
            onEvidence={addSubmissionEvidence}
          />
        )}
        {parentOverrides === null ? null : (
          <ParentOverrideDecisionPanel
            connectivity={connectivity}
            studentName={snapshot.student.displayName}
            overrides={parentOverrides}
            stepLabel={(stepId) =>
              queue?.steps.find((step) => step.id === stepId)?.instruction ??
              "Step unavailable"
            }
            assignmentLabel={(assignmentId) =>
              queue?.assignments.find(
                (assignment) => assignment.id === assignmentId,
              )?.title ?? "Assignment unavailable"
            }
            onDecision={decideParentOverride}
          />
        )}
        <DayCloseoutRegion
          snapshot={snapshot}
          role="parent"
          sessionClient={sessionClient}
          connectivity={connectivity}
          overrides={parentOverrides}
          onSafeAlternative={async () => undefined}
        />
        {conflictChoiceMessage === null ? null : (
          <p role="status" aria-live="polite">
            {conflictChoiceMessage}
          </p>
        )}
        {publishedReconciliation === null ? null : (
          <section
            className="published-reconciliation"
            aria-labelledby="published-reconciliation-title"
          >
            <p className="status-label">Unsaved Draft preserved</p>
            <h2
              id="published-reconciliation-title"
              ref={reconciliationHeading}
              tabIndex={-1}
            >
              Review your unsaved Draft
            </h2>
            <p>
              The Daily Queue became{" "}
              <strong>{publishedReconciliation.remote.status}</strong> before
              these entries were saved. They were not published. Copy them below
              before acknowledging this change.
            </p>
            <label htmlFor="published-reconciliation-copy">
              Copy of your unsaved Daily Queue
            </label>
            <textarea
              id="published-reconciliation-copy"
              ref={reconciliationCopy}
              readOnly
              rows={12}
              value={reconciliationText}
            />
            <div className="conflict-actions">
              <button
                type="button"
                onClick={() => {
                  const copy =
                    navigator.clipboard?.writeText(reconciliationText);
                  if (copy === undefined) {
                    reconciliationCopy.current?.select();
                    setConflictChoiceMessage(
                      "The preserved entries are selected. Copy them with your device’s copy command.",
                    );
                    return;
                  }
                  void copy
                    .then(() =>
                      setConflictChoiceMessage(
                        "Your unsaved Daily Queue was copied.",
                      ),
                    )
                    .catch(() => {
                      reconciliationCopy.current?.select();
                      setConflictChoiceMessage(
                        "The preserved entries are selected. Copy them with your device’s copy command.",
                      );
                    });
                }}
              >
                Copy unsaved Daily Queue
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  latestPlannerRef.current = null;
                  setPublishedReconciliation(null);
                  setConflictChoiceMessage(
                    "Published Queue status acknowledged.",
                  );
                  setFocusStatusAfterAcknowledgment(true);
                }}
              >
                Acknowledge published Queue
              </button>
            </div>
          </section>
        )}
        {draftConflict !== null && remoteConflictDraft !== null ? (
          <section className="conflict-panel" aria-labelledby="conflict-title">
            <div>
              <p className="status-label">Draft conflict</p>
              <h2 id="conflict-title">Another Draft is available</h2>
              <p>
                Compare both versions. Nothing will be replaced until you choose
                which Draft to keep.
              </p>
            </div>
            <dl className="conflict-comparison">
              {[
                [
                  "Courses",
                  draftConflict.local.courses
                    .map((course) => course.title)
                    .join("; "),
                  remoteConflictDraft.courses
                    .map((course) => course.title)
                    .join("; "),
                ],
                [
                  "Assignments",
                  draftConflict.local.assignments
                    .map((assignment) => assignment.title)
                    .join("; "),
                  remoteConflictDraft.assignments
                    .map((assignment) => assignment.title)
                    .join("; "),
                ],
                [
                  "Schedule",
                  `${draftConflict.local.localDate}, ${draftConflict.local.startTime}–${draftConflict.local.endTime}`,
                  `${remoteConflictDraft.localDate}, ${remoteConflictDraft.startTime}–${remoteConflictDraft.endTime}`,
                ],
                [
                  "Ordered Steps",
                  draftConflict.local.steps
                    .map((step) => step.instruction)
                    .join("; "),
                  remoteConflictDraft.steps
                    .map((step) => step.instruction)
                    .join("; "),
                ],
              ].map(([label, localValue, remoteValue]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>
                    <span>
                      <strong>Your entries:</strong> {localValue}
                    </span>
                    <span>
                      <strong>Newer Draft:</strong> {remoteValue}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
            <div className="conflict-actions">
              <button
                type="button"
                onClick={() => {
                  setDraftConflict(null);
                  setConflictChoiceMessage(
                    "Your entries are selected. Save for later to replace the newer version.",
                  );
                }}
              >
                Keep my entries
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setPlannerVersion((version) => version + 1);
                  setDraftConflict(null);
                  setConflictChoiceMessage(
                    "The newer Draft is loaded in the planner.",
                  );
                }}
              >
                Load newer Draft
              </button>
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}

function SupportingContext({
  label,
  values,
}: {
  label: "Resources" | "Topics";
  values: Array<{ id: string; label: string; url?: string | undefined }>;
}) {
  if (values.length === 0) return null;
  return (
    <div className="supporting-context">
      <dt>{label}</dt>
      <dd>
        {values.map((value, index) => (
          <span key={value.id}>
            {index === 0 ? "" : ", "}
            {value.label}
          </span>
        ))}
      </dd>
    </div>
  );
}

function safeResourceUrl(value: string | undefined): value is string {
  if (value === undefined) return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function dueContext(dueAt: string | undefined, timezone: string) {
  if (dueAt === undefined) return "No due date provided";
  return `Due ${new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(dueAt))}`;
}

function changedAtTime(changedAt: string, timezone: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(changedAt));
}

function resourceLaunchState(
  snapshot: HouseholdSnapshot,
  queue: DailyQueueSnapshot | null,
  workState: StudentWorkStateSnapshot | null,
): StudentWorkState | null {
  if (workState === null) return null;
  const steps = new Map(queue?.steps.map((step) => [step.id, step]));
  return {
    studentId: snapshot.student.id,
    revision: workState.revision,
    queue:
      queue === null
        ? null
        : {
            id: queue.id,
            revision: queue.revision,
            state: "Active",
            items: queue.queueItems.map((item) => ({
              id: item.id,
              stepId: item.stepId,
              eligibility: "Eligible",
              resourceIds: steps.get(item.stepId)?.resourceIds ?? [],
            })),
          },
    activeStepId: workState.activeStepId,
    activeQueueItemId: workState.activeQueueItemId,
    changedBy: workState.changedBy,
    changedAt: workState.changedAt,
  };
}

function StudentSurface({
  snapshot,
  workspace,
  sessionClient,
  resourceLauncher,
  connectivity,
  onConnectivityConflict,
  theme,
  onThemeChange,
}: {
  snapshot: HouseholdSnapshot;
  workspace: boolean;
  sessionClient: SessionClient;
  resourceLauncher: ResourceLauncher;
  connectivity: FocusBlockConnectivity;
  onConnectivityConflict: () => void;
  theme: ThemeId;
  onThemeChange: (theme: ThemeId) => void;
}) {
  const incomingQueue =
    snapshot.dailyQueue?.status === "active" ? snapshot.dailyQueue : null;
  const [queue, setQueue] = useState(incomingQueue);
  const steps = new Map(queue?.steps.map((step) => [step.id, step]));
  const assignments = new Map(
    queue?.assignments.map((assignment) => [assignment.id, assignment]),
  );
  const courses = new Map(queue?.courses.map((course) => [course.id, course]));
  const resources = new Map(queue?.resources.map((item) => [item.id, item]));
  const topics = new Map(queue?.topics.map((item) => [item.id, item]));
  const [workState, setWorkState] = useState(snapshot.studentWorkState);
  const [focusBlock, setFocusBlock] = useState(snapshot.focusBlock ?? null);
  const [studentRecovery, setStudentRecovery] = useState(
    snapshot.studentRecovery ?? null,
  );
  const [schoolworkProgress, setSchoolworkProgress] = useState(
    snapshot.schoolworkProgress ?? null,
  );
  const [parentOverrides, setParentOverrides] = useState(
    snapshot.parentOverrides ?? null,
  );
  const [focusBlockNotice, setFocusBlockNotice] = useState<string | null>(null);
  const [pendingQueueItemId, setPendingQueueItemId] = useState<string | null>(
    null,
  );
  const [pendingReorderItemId, setPendingReorderItemId] = useState<
    string | null
  >(null);
  const [pendingFocusTarget, setPendingFocusTarget] = useState<string | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [schoolworkNotice, setSchoolworkNotice] = useState<string | null>(null);
  const [schoolworkConflictNotice, setSchoolworkConflictNotice] = useState<
    string | null
  >(null);
  const [conflictNotice, setConflictNotice] = useState<{
    message: string;
    authority: StudentWorkStateSnapshot;
  } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const observedRevision = useRef(snapshot.studentWorkState?.revision ?? null);
  const observedFocusBlock = useRef(
    snapshot.focusBlock === null || snapshot.focusBlock === undefined
      ? null
      : `${snapshot.focusBlock.id}:${snapshot.focusBlock.revision}:${snapshot.focusBlock.observedAt}`,
  );
  const observedRecovery = useRef(
    snapshot.studentRecovery === null || snapshot.studentRecovery === undefined
      ? null
      : `${snapshot.studentRecovery.revision}:${snapshot.studentRecovery.observedAt}`,
  );
  const activeHeading = useRef<HTMLHeadingElement>(null);
  const actionErrorRef = useRef<HTMLParagraphElement>(null);
  const schoolworkNoticeRef = useRef<HTMLParagraphElement>(null);
  const focusNextActive = useRef(false);
  const importedStartRequest = useRef<{
    requestId: string;
    sourceItemKey: string;
    expectedRevision: number;
    intent: "start" | "switch";
  } | null>(null);

  async function startImportedActivity(
    candidate: CanvasAssignmentCandidateInput,
  ) {
    if (
      connectivity !== "connected" ||
      workState === null ||
      sessionClient.startImportedActivity === undefined
    ) {
      throw new Error(
        "Reconnect to confirm your day before starting the timer.",
      );
    }
    let command = importedStartRequest.current;
    if (
      command === null ||
      command.sourceItemKey !== candidate.sourceItemKey ||
      command.expectedRevision !== workState.revision
    ) {
      command = {
        requestId: crypto.randomUUID(),
        sourceItemKey: candidate.sourceItemKey,
        expectedRevision: workState.revision,
        intent: workState.activeStepId === null ? "start" : "switch",
      };
      importedStartRequest.current = command;
    }
    const refreshed = await sessionClient.startImportedActivity(command);
    importedStartRequest.current = null;
    focusNextActive.current = true;
    setQueue(
      refreshed.dailyQueue?.status === "active" ? refreshed.dailyQueue : null,
    );
    setWorkState(refreshed.studentWorkState);
    setFocusBlock(refreshed.focusBlock ?? null);
    setStudentRecovery(refreshed.studentRecovery ?? null);
    setSchoolworkProgress(refreshed.schoolworkProgress ?? null);
    setAnnouncement(
      "Your work timer is ready. Canvas opens only when you choose its link.",
    );
  }

  useEffect(() => {
    setQueue(incomingQueue);
    setPendingReorderItemId(null);
  }, [incomingQueue]);

  useEffect(() => {
    if (pendingFocusTarget === null) return;
    setPendingFocusTarget(null);
    if (pendingFocusTarget === "action-error") {
      actionErrorRef.current?.focus();
      return;
    }
    const stepId = queue?.queueItems.find(
      (item) => item.id === pendingFocusTarget,
    )?.stepId;
    const target =
      document.getElementById(`queue-item-${pendingFocusTarget}`) ??
      (stepId === undefined ? null : document.getElementById(`step-${stepId}`));
    target?.focus();
  }, [pendingFocusTarget, queue, actionErrorRef]);

  const progressSteps = new Map(
    schoolworkProgress?.steps.map((step) => [step.id, step]),
  );
  const fullOrderedItems = [...(queue?.queueItems ?? [])].sort(
    (left, right) => left.position - right.position,
  );
  const orderedItems = fullOrderedItems.filter((item) => {
    const progressStep = progressSteps.get(item.stepId);
    return (
      progressStep === undefined ||
      (!progressStep.replacedBySplit &&
        !["done", "deferred", "skipped"].includes(progressStep.status))
    );
  });
  const activeItem =
    queue !== null && workState?.activeQueueId === queue.id
      ? orderedItems.find(
          (item) =>
            item.id === workState.activeQueueItemId &&
            item.stepId === workState.activeStepId,
        )
      : undefined;
  const launchPolicyState = resourceLaunchState(snapshot, queue, workState);
  const nextItem = activeItem === undefined ? orderedItems[0] : undefined;
  const remainingItems = orderedItems.filter(
    (item) => item.id !== activeItem?.id && item.id !== nextItem?.id,
  );
  const recoveryInProgress =
    studentRecovery?.break?.state === "running" ||
    studentRecovery?.transition?.state === "running";

  useEffect(() => {
    const incoming = snapshot.studentWorkState;
    const incomingRevision = incoming?.revision ?? null;
    if (incomingRevision === observedRevision.current) return;
    observedRevision.current = incomingRevision;
    const changed =
      incoming?.activeStepId !== (workState?.activeStepId ?? null);
    setWorkState(incoming);
    setPendingQueueItemId(null);
    setActionError(null);
    setConflictNotice((current) => {
      if (current === null || incoming === null) return null;
      const authority = current.authority;
      return incoming.revision === authority.revision &&
        incoming.activeQueueId === authority.activeQueueId &&
        incoming.activeQueueItemId === authority.activeQueueItemId &&
        incoming.activeStepId === authority.activeStepId
        ? current
        : null;
    });
    if (changed && incoming?.activeStepId !== null && incoming !== null) {
      const nextStep = steps.get(incoming.activeStepId);
      if (nextStep !== undefined) {
        focusNextActive.current = true;
        setAnnouncement(`Active Step changed to ${nextStep.instruction}.`);
      }
    }
  }, [snapshot.studentWorkState, steps, workState?.activeStepId]);

  useEffect(() => {
    const incoming = snapshot.focusBlock ?? null;
    const incomingKey =
      incoming === null
        ? null
        : `${incoming.id}:${incoming.revision}:${incoming.observedAt}`;
    if (incomingKey === observedFocusBlock.current) return;
    observedFocusBlock.current = incomingKey;
    setFocusBlock(incoming);
    setFocusBlockNotice(null);
  }, [snapshot.focusBlock]);

  useEffect(() => {
    const incoming = snapshot.studentRecovery ?? null;
    const incomingKey =
      incoming === null ? null : `${incoming.revision}:${incoming.observedAt}`;
    if (incomingKey === observedRecovery.current) return;
    observedRecovery.current = incomingKey;
    setStudentRecovery(incoming);
  }, [snapshot.studentRecovery]);

  useEffect(() => {
    setSchoolworkProgress(snapshot.schoolworkProgress ?? null);
  }, [snapshot.schoolworkProgress]);

  useEffect(() => {
    setParentOverrides(snapshot.parentOverrides ?? null);
  }, [snapshot.parentOverrides]);

  useEffect(() => {
    if (!focusNextActive.current) return;
    focusNextActive.current = false;
    activeHeading.current?.focus();
  }, [workState?.activeQueueItemId]);

  useEffect(() => {
    if (schoolworkNotice !== null) schoolworkNoticeRef.current?.focus();
  }, [schoolworkNotice]);

  function contextFor(item: DailyQueueSnapshot["queueItems"][number]) {
    const step = steps.get(item.stepId);
    if (step === undefined) return null;
    const assignment = assignments.get(step.assignmentId);
    const course =
      assignment === undefined ? undefined : courses.get(assignment.courseId);
    const stepResources = step.resourceIds.flatMap((id) => {
      const resource = resources.get(id);
      return resource !== undefined &&
        resource.assignmentId === step.assignmentId
        ? [resource]
        : [];
    });
    const stepTopics = step.topicIds.flatMap((id) => {
      const topic = topics.get(id);
      return topic !== undefined && topic.assignmentId === step.assignmentId
        ? [topic]
        : [];
    });
    return { item, step, assignment, course, stepResources, stepTopics };
  }

  async function activate(item: DailyQueueSnapshot["queueItems"][number]) {
    if (connectivity !== "connected") {
      setActionError(
        "Live state is not confirmed. Reconnect before starting or switching a Step so a newer choice is not replaced.",
      );
      return;
    }
    const start = sessionClient.startActiveStep;
    if (queue === null || workState === null || start === undefined) {
      setActionError(
        "Starting a Step is temporarily unavailable. Nothing changed.",
      );
      return;
    }
    const intent = workState.activeStepId === null ? "start" : "switch";
    setActionError(null);
    setConflictNotice(null);
    setPendingQueueItemId(item.id);
    try {
      const accepted = await start(
        snapshot.student.id,
        queue.id,
        item.id,
        workState.revision,
        intent,
      );
      setWorkState(accepted);
      setPendingQueueItemId(null);
      focusNextActive.current = true;
      const acceptedStep = steps.get(accepted.activeStepId ?? "");
      setAnnouncement(
        acceptedStep === undefined
          ? `${intent === "start" ? "Start" : "Switch"} accepted.`
          : `Active Step changed to ${acceptedStep.instruction}.`,
      );
    } catch (reason) {
      setPendingQueueItemId(null);
      if (reason instanceof ActiveStepConflictError) {
        onConnectivityConflict();
        setWorkState(reason.snapshot);
        focusNextActive.current = reason.snapshot.activeStepId !== null;
        const winner =
          reason.authoritativeWinner === null
            ? undefined
            : steps.get(reason.authoritativeWinner.stepId);
        const changedBy = reason.snapshot.changedBy ?? reason.changedBy;
        const actor = changedBy === "parent" ? "parent" : "student";
        setConflictNotice({
          authority: reason.snapshot,
          message:
            winner === undefined
              ? `A ${actor} session changed the Active Step. Your ${intent === "start" ? "Start" : "Switch"} did not replace it.`
              : `A ${actor} session started ${winner.instruction} at ${changedAtTime(reason.snapshot.changedAt, snapshot.student.timezone)}. Your ${intent === "start" ? "Start" : "Switch"} did not replace it.`,
        });
        return;
      }
      setActionError(
        reason instanceof Error
          ? reason.message
          : "The Active Step could not be changed. Nothing changed.",
      );
    }
  }

  async function reorderQueueItem(itemId: string, direction: -1 | 1) {
    if (connectivity !== "connected") {
      setActionError(
        "Live state is not confirmed. Reconnect before changing the Daily Queue order.",
      );
      return;
    }
    const reorder = sessionClient.reorderDailyQueue;
    if (queue === null || reorder === undefined) {
      setActionError("Student Queue ordering is temporarily unavailable.");
      return;
    }
    const eligible = orderedItems.filter(
      ({ planning }) => planning.studentReorderEligible === true,
    );
    const eligibleIndex = eligible.findIndex(({ id }) => id === itemId);
    const destination = eligible[eligibleIndex + direction];
    if (eligibleIndex < 0 || destination === undefined) return;
    const order = [...fullOrderedItems];
    const currentIndex = order.findIndex(({ id }) => id === itemId);
    const destinationIndex = order.findIndex(({ id }) => id === destination.id);
    [order[currentIndex], order[destinationIndex]] = [
      order[destinationIndex]!,
      order[currentIndex]!,
    ];
    setPendingReorderItemId(itemId);
    setActionError(null);
    try {
      const accepted = await reorder(
        queue.id,
        queue.revision,
        order.map(({ id }) => id),
      );
      setQueue(accepted);
      setAnnouncement("Daily Queue order updated.");
      setPendingFocusTarget(itemId);
    } catch (reason) {
      if (reason instanceof DailyQueueConflictError) {
        setQueue(reason.snapshot);
        onConnectivityConflict();
        setActionError(
          "The Daily Queue changed on another surface. The newer order is shown; review it before trying again.",
        );
      } else {
        setActionError(
          reason instanceof Error
            ? reason.message
            : "The Daily Queue order could not be changed.",
        );
      }
      setPendingFocusTarget("action-error");
    } finally {
      setPendingReorderItemId(null);
    }
  }

  async function changeFocusBlock(command: FocusBlockUiCommand) {
    const runCommand = sessionClient.runFocusBlockCommand;
    if (focusBlock === null || runCommand === undefined) {
      throw new Error(
        "Focus Block controls are temporarily unavailable. The timer shown has not been changed.",
      );
    }
    setFocusBlockNotice(null);
    try {
      const accepted = await runCommand(
        snapshot.student.id,
        focusBlock.id,
        focusBlock.revision,
        command,
      );
      observedFocusBlock.current = `${accepted.id}:${accepted.revision}:${accepted.observedAt}`;
      setFocusBlock(accepted);
    } catch (error) {
      if (error instanceof FocusBlockConflictError) {
        onConnectivityConflict();
        const current = error.snapshot;
        const actor = error.changedBy === "parent" ? "parent" : "student";
        const conflictMessage = `Another ${actor} session changed this Focus Block. The server snapshot is shown; try again if this action is still needed.`;
        observedFocusBlock.current =
          current === null
            ? null
            : `${current.id}:${current.revision}:${current.observedAt}`;
        setFocusBlock(current);
        if (current === null) setFocusBlockNotice(conflictMessage);
        throw new Error(conflictMessage, { cause: error });
      }
      throw error;
    }
  }

  async function changeRecovery(command: RecoveryUiCommand) {
    const runCommand = sessionClient.runRecoveryCommand;
    if (studentRecovery === null || runCommand === undefined) {
      throw new Error(
        "Break and recovery controls are temporarily unavailable. Nothing changed.",
      );
    }
    try {
      const accepted = await runCommand(
        snapshot.student.id,
        studentRecovery.revision,
        command,
      );
      observedRecovery.current = `${accepted.revision}:${accepted.observedAt}`;
      setStudentRecovery(accepted);
      const refreshed = await sessionClient.load();
      if (refreshed.role === "student") {
        setFocusBlock(refreshed.focusBlock ?? null);
        setStudentRecovery(refreshed.studentRecovery ?? accepted);
      }
    } catch (reason) {
      if (reason instanceof RecoveryConflictError) {
        observedRecovery.current = `${reason.snapshot.revision}:${reason.snapshot.observedAt}`;
        setStudentRecovery(reason.snapshot);
        const actor = reason.changedBy === "parent" ? "parent" : "student";
        throw new Error(
          `Another ${actor} session changed Break or recovery state. The server state is shown; try again if this action is still needed.`,
          { cause: reason },
        );
      }
      throw reason;
    }
  }

  async function changeSchoolwork(command: SchoolworkUiCommand) {
    const runCommand = sessionClient.runSchoolworkCommand;
    if (runCommand === undefined) {
      throw new Error(
        "Schoolwork outcome controls are temporarily unavailable.",
      );
    }
    try {
      const accepted = await runCommand(snapshot.student.id, command);
      setSchoolworkProgress(accepted);
      const affectedStep =
        "stepId" in command ? steps.get(command.stepId) : undefined;
      if (command.intent === "done") {
        setSchoolworkNotice(
          `Done recorded for ${affectedStep?.instruction ?? "this Step"}. Assignment progress was recalculated from the required Steps.`,
        );
      } else if (command.intent === "split-step") {
        setSchoolworkNotice(
          `Split recorded for ${affectedStep?.instruction ?? "this Step"}. The selected smaller Step is now active.`,
        );
      }
      return accepted;
    } catch (error) {
      if (error instanceof SchoolworkConflictError) {
        setSchoolworkProgress(error.snapshot);
        const actor = error.changedBy === "parent" ? "parent" : "student";
        const message = `Another ${actor} session changed this work. The newer server status is shown; your action was not applied. Review it and try again if the action is still needed.`;
        setSchoolworkConflictNotice(message);
        throw new Error(message, { cause: error });
      }
      throw error;
    }
  }

  function rememberRequestedOverride(
    override: ParentOverrideStateSnapshot["overrides"][number],
    changedAt: string,
  ) {
    setParentOverrides((current) => ({
      revision: (current?.revision ?? 0) + 1,
      changedBy: "student",
      changedAt,
      overrides: [
        ...(current?.overrides.filter(({ id }) => id !== override.id) ?? []),
        override,
      ],
    }));
  }

  async function refreshStudentOperation() {
    try {
      const refreshed = await sessionClient.load();
      if (refreshed.role !== "student") return;
      setWorkState(refreshed.studentWorkState);
      setFocusBlock(refreshed.focusBlock ?? null);
      setStudentRecovery(refreshed.studentRecovery ?? null);
      setSchoolworkProgress(refreshed.schoolworkProgress ?? null);
      setParentOverrides(refreshed.parentOverrides ?? null);
    } catch {
      setSchoolworkNotice(
        "The action was accepted, but live details are reconnecting. The server result shown remains authoritative.",
      );
    }
  }

  async function changeProtectedSkip(command: SkipUiCommand) {
    const runSkip = sessionClient.runProtectedSkip;
    if (runSkip === undefined) {
      throw new Error("Skip requests are temporarily unavailable.");
    }
    if (connectivity !== "connected") {
      throw new Error(
        "Reconnect before requesting or using a Parent Override. Nothing was changed.",
      );
    }
    const result = await runSkip(snapshot.student.id, command);
    if (result.status === "override-required") {
      rememberRequestedOverride(result.override, result.serverTime);
      return;
    }
    if (result.status === "accepted") {
      setSchoolworkProgress(result.snapshot);
      setSchoolworkNotice(
        "Approved Skip recorded. This Step remains visible for replan and was not marked Done or submitted.",
      );
      void refreshStudentOperation();
      return;
    }
    if (result.error.code === "REVISION_CONFLICT") {
      setSchoolworkProgress(result.error.snapshot);
      const message =
        "Schoolwork changed in another session. The newer server status is shown; the Skip was not applied.";
      setSchoolworkConflictNotice(message);
      throw new Error(message);
    }
    throw new Error(result.error.message);
  }

  async function cancelParentOverride(command: OverrideCancellationUiCommand) {
    const cancel = sessionClient.cancelParentOverride;
    if (cancel === undefined) {
      throw new Error("Cancelling a Skip request is temporarily unavailable.");
    }
    if (connectivity !== "connected") {
      throw new Error(
        "Reconnect and review the latest request before cancelling it.",
      );
    }
    const result = await cancel(snapshot.student.id, command);
    if (result.status === "accepted") {
      setParentOverrides(result.snapshot);
      return;
    }
    if (result.error.snapshot !== undefined) {
      setParentOverrides(result.error.snapshot);
    }
    throw new Error(result.error.message);
  }

  async function useSafeAlternative(alternative: SafeAlternative) {
    if (alternative === "continue" || alternative === "need-help") {
      if (
        schoolworkProgress === null ||
        workState === null ||
        activeItem === undefined
      ) {
        throw new Error(
          "The current Step state is unavailable. Nothing changed.",
        );
      }
      await changeSchoolwork({
        intent: alternative,
        stepId: activeItem.stepId,
        expectedRevision: schoolworkProgress.revision,
        expectedWorkStateRevision: workState.revision,
      });
      return alternative === "continue"
        ? "Continue recorded. The active Step and Parent Override request are preserved."
        : "Help requested. The active Step and Parent Override request are preserved.";
    }
    if (alternative === "report-problem") {
      return "Use the visible problem options to request Help or contact a trusted adult. Nothing was sent or saved as a separate report.";
    }
    return "Use the visible safety exit to leave this schoolwork screen. The active Step and Skip request remain unchanged.";
  }

  function renderContext(
    context: NonNullable<ReturnType<typeof contextFor>>,
    isActive: boolean,
  ) {
    const { item, step, assignment, course, stepResources, stepTopics } =
      context;
    const headingId = `step-${item.id}`;
    const launchableResources =
      isActive && workspace && launchPolicyState !== null
        ? stepResources.filter(
            (resource) =>
              safeResourceUrl(resource.url) &&
              activeStepWorkday.authorizeActiveStepResourceLaunch(
                launchPolicyState,
                resource.id,
              ).ok,
          )
        : [];
    return (
      <>
        <div className="queue-item-heading">
          {!isActive ? (
            <span className="queue-position" aria-hidden="true">
              {item.position}
            </span>
          ) : null}
          <div>
            <p className={isActive ? "status-label" : "queue-context"}>
              {isActive
                ? "Active Step"
                : `${course?.title ?? "Course"} · ${assignment?.title ?? "Assignment"}`}
            </p>
            {isActive ? (
              <>
                <p className="queue-context">
                  {course?.title ?? "Course"} ·{" "}
                  {assignment?.title ?? "Assignment"}
                </p>
                <h2 id={headingId} ref={activeHeading} tabIndex={-1}>
                  {step.instruction}
                </h2>
              </>
            ) : (
              <h2 id={headingId} tabIndex={-1}>
                {step.instruction}
              </h2>
            )}
          </div>
        </div>
        <p className="done-means">
          <strong>Done means:</strong> {step.completionCondition}
        </p>
        <div className="queue-badges" aria-label="Step planning">
          <span>
            {item.planning.minMinutes}–{item.planning.maxMinutes} minutes
          </span>
          <span>
            {dueContext(
              assignment?.dueAt,
              queue?.timezone ?? snapshot.student.timezone,
            )}
          </span>
          <span>{step.required ? "Required" : "Optional"}</span>
          {item.planning.mustFinish ? <span>Must finish today</span> : null}
        </div>
        {!isActive && item.planning.studentReorderEligible === true ? (
          <div
            className="student-reorder-actions"
            aria-label={`Reorder ${step.instruction}`}
          >
            {(() => {
              const eligible = orderedItems.filter(
                ({ planning }) => planning.studentReorderEligible === true,
              );
              const index = eligible.findIndex(({ id }) => id === item.id);
              return (
                <>
                  <button
                    type="button"
                    aria-label={`Move ${step.instruction} earlier`}
                    disabled={
                      connectivity !== "connected" ||
                      pendingReorderItemId !== null ||
                      index <= 0
                    }
                    onClick={() => void reorderQueueItem(item.id, -1)}
                  >
                    Move earlier
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${step.instruction} later`}
                    disabled={
                      connectivity !== "connected" ||
                      pendingReorderItemId !== null ||
                      index < 0 ||
                      index === eligible.length - 1
                    }
                    onClick={() => void reorderQueueItem(item.id, 1)}
                  >
                    Move later
                  </button>
                </>
              );
            })()}
          </div>
        ) : null}
        <dl>
          <SupportingContext label="Resources" values={stepResources} />
          <SupportingContext label="Topics" values={stepTopics} />
        </dl>
        {isActive && workspace ? (
          <div className="resource-actions" aria-label="Active Step Resources">
            {launchableResources.length > 0 ? (
              launchableResources.map((resource) => {
                const url = resource.url;
                return safeResourceUrl(url) ? (
                  <button
                    key={resource.id}
                    type="button"
                    onClick={() => {
                      const decision =
                        launchPolicyState === null
                          ? { ok: false as const }
                          : activeStepWorkday.authorizeActiveStepResourceLaunch(
                              launchPolicyState,
                              resource.id,
                            );
                      if (!decision.ok) {
                        setActionError(
                          "This Resource is no longer attached to the Active Step. Refresh and try again.",
                        );
                        return;
                      }
                      if (
                        !resourceLauncher(url, "_blank", "noopener,noreferrer")
                      ) {
                        setActionError(
                          `${resource.label} did not open. Allow pop-ups for Daylight, then try Open ${resource.label} again.`,
                        );
                        return;
                      }
                      setActionError(null);
                      setAnnouncement(
                        `Opened ${resource.label} in a new browser tab.`,
                      );
                    }}
                  >
                    Open {resource.label}
                  </button>
                ) : null;
              })
            ) : (
              <p className="resource-unavailable">
                This Active Step has no safe Resource link to open.
              </p>
            )}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className={`app-shell${workspace ? " workspace-shell" : ""}`}>
      <SurfaceHeader
        snapshot={snapshot}
        connectivity={connectivity}
        studentSurface={workspace ? "workspace" : "console"}
        theme={theme}
        onThemeChange={onThemeChange}
      />
      <main id="main-content" className="surface-main">
        <p className="eyebrow">
          {workspace ? "Managed Workspace" : "Student Console"}
        </p>
        {queue === null ? (
          <>
            <h1>
              {sessionClient.loadStudentSchoolwork !== undefined
                ? "Your schoolwork"
                : workspace
                  ? "Ready when your plan is"
                  : "Your Daily Queue is clear"}
            </h1>
            <p className="lead">
              {sessionClient.loadStudentSchoolwork !== undefined
                ? "Choose Start working on an activity below. Your timer and progress will appear automatically."
                : workspace
                  ? "The active Step and its school Resource will stay centered here."
                  : "Choose Start working on an imported activity below. Your timer and progress will appear here automatically."}
            </p>
          </>
        ) : (
          <section
            className="student-queue"
            aria-labelledby="daily-queue-title"
          >
            <p className="queue-context">Today’s ordered schoolwork</p>
            <h1 id="daily-queue-title">Daily Queue</h1>
            <p className="lead">
              {activeItem === undefined
                ? "Choose one concrete Step to begin. Inspecting work never starts or opens it."
                : workspace
                  ? "Your current Step and its launchable school Resource stay centered here."
                  : "Your current Step stays first while the rest of the Daily Queue remains available to inspect."}
            </p>
            {announcement === "" ? null : (
              <p
                className="student-action-status"
                role="status"
                aria-live="polite"
              >
                {announcement}
              </p>
            )}
            {actionError === null ? null : (
              <p
                className="active-step-error"
                role="alert"
                ref={actionErrorRef}
                tabIndex={-1}
              >
                {actionError}
              </p>
            )}
            {schoolworkNotice === null ? null : (
              <p
                className="schoolwork-notice"
                role="status"
                aria-live="polite"
                ref={schoolworkNoticeRef}
                tabIndex={-1}
              >
                {schoolworkNotice}
              </p>
            )}
            {schoolworkConflictNotice === null ? null : (
              <div className="active-step-error" role="alert">
                <p>{schoolworkConflictNotice}</p>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setSchoolworkConflictNotice(null)}
                >
                  Dismiss schoolwork conflict
                </button>
              </div>
            )}
            {conflictNotice === null ? null : (
              <div className="active-step-error conflict-notice" role="alert">
                <span>{conflictNotice.message}</span>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setConflictNotice(null)}
                >
                  Dismiss conflict message
                </button>
              </div>
            )}
            {workState === null ? (
              <p className="active-step-error" role="alert">
                Active Step status is unavailable. Refresh before starting a
                Step so a newer choice is not replaced.
              </p>
            ) : null}
            {activeItem === undefined ? null : (
              <section
                className="active-step-card"
                aria-label="Current Active Step"
              >
                {renderContext(contextFor(activeItem)!, true)}
                {focusBlockNotice === null ? null : (
                  <div className="focus-command-error" role="alert">
                    <p>{focusBlockNotice}</p>
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => setFocusBlockNotice(null)}
                    >
                      Dismiss timer message
                    </button>
                  </div>
                )}
                {focusBlock === null ? (
                  <p className="focus-command-error">
                    Focus Block status is unavailable. Timer controls will
                    appear after the authoritative snapshot arrives.
                  </p>
                ) : (
                  <FocusBlockPanel
                    connectivity={connectivity}
                    snapshot={focusBlock}
                    timezone={snapshot.student.timezone}
                    onCommand={changeFocusBlock}
                  />
                )}
                {studentRecovery === null ? (
                  <p className="focus-command-error">
                    Break and recovery status is unavailable. Refresh before
                    changing the current work state.
                  </p>
                ) : (
                  <BreakRecoveryPanel
                    connectivity={connectivity}
                    snapshot={studentRecovery}
                    onCommand={changeRecovery}
                  />
                )}
                {schoolworkProgress === null || workState === null ? null : (
                  <StudentOutcomeControls
                    connectivity={connectivity}
                    progress={schoolworkProgress}
                    stepId={activeItem.stepId}
                    workStateRevision={workState.revision}
                    onCommand={changeSchoolwork}
                  />
                )}
                {schoolworkProgress === null ||
                workState === null ||
                parentOverrides === null ? null : (
                  <StudentSkipPanel
                    connectivity={connectivity}
                    stepId={activeItem.stepId}
                    expectedSchoolworkRevision={schoolworkProgress.revision}
                    expectedWorkStateRevision={workState.revision}
                    overrides={parentOverrides}
                    onSkip={changeProtectedSkip}
                    onCancel={cancelParentOverride}
                    onSafeAlternative={useSafeAlternative}
                  />
                )}
              </section>
            )}
            {activeItem === undefined || workspace ? null : (
              <h2 className="remaining-queue-title">Other Queue Items</h2>
            )}
            {activeItem !== undefined &&
            recoveryInProgress &&
            remainingItems.length > 0 ? (
              <p className="queue-footnote">
                Finish the current Break or transition before switching the
                Active Step.
              </p>
            ) : null}
            {activeItem !== undefined && workspace ? (
              <section
                className="workspace-switcher"
                aria-labelledby="workspace-switch-title"
              >
                <h2 id="workspace-switch-title">Switch Step</h2>
                <ul className="compact-switch-list">
                  {remainingItems.map((item) => {
                    const context = contextFor(item);
                    if (context === null) return null;
                    return (
                      <li key={item.id}>
                        <span>{context.step.instruction}</span>
                        <button
                          className="secondary-button"
                          type="button"
                          disabled={
                            pendingQueueItemId !== null ||
                            recoveryInProgress ||
                            connectivity !== "connected"
                          }
                          onClick={() => void activate(item)}
                        >
                          {pendingQueueItemId === item.id
                            ? "Switching…"
                            : `Switch to ${context.step.instruction}`}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : (
              <ol className="queue-items">
                {nextItem === undefined
                  ? null
                  : (() => {
                      const context = contextFor(nextItem);
                      if (context === null) return null;
                      return (
                        <li
                          key={nextItem.id}
                          id={`queue-item-${nextItem.id}`}
                          className="queue-item next-step-card"
                          tabIndex={-1}
                        >
                          {renderContext(context, false)}
                          <button
                            className="step-start-action"
                            type="button"
                            disabled={
                              pendingQueueItemId !== null ||
                              workState === null ||
                              connectivity !== "connected"
                            }
                            onClick={() => void activate(nextItem)}
                          >
                            {pendingQueueItemId === nextItem.id
                              ? "Starting…"
                              : `Start ${context.step.instruction}`}
                          </button>
                        </li>
                      );
                    })()}
                {remainingItems.map((item) => {
                  const context = contextFor(item);
                  if (context === null) return null;
                  return (
                    <li
                      key={item.id}
                      id={`queue-item-${item.id}`}
                      className="queue-item"
                      tabIndex={-1}
                    >
                      <details>
                        <summary>Inspect {context.step.instruction}</summary>
                        <div className="inactive-step-context">
                          {renderContext(context, false)}
                        </div>
                      </details>
                      {activeItem === undefined ? null : (
                        <button
                          className="step-start-action secondary-button"
                          type="button"
                          disabled={
                            pendingQueueItemId !== null ||
                            recoveryInProgress ||
                            connectivity !== "connected"
                          }
                          onClick={() => void activate(item)}
                        >
                          {pendingQueueItemId === item.id
                            ? "Switching…"
                            : `Switch to ${context.step.instruction}`}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        )}
        {sessionClient.loadStudentSchoolwork === undefined ? null : (
          <SchoolworkOverview
            audience="student"
            loadCandidates={sessionClient.loadStudentSchoolwork}
            timezone={snapshot.student.timezone}
            {...(sessionClient.startImportedActivity === undefined
              ? {}
              : { onStart: startImportedActivity })}
            startDisabled={
              connectivity !== "connected" ||
              workState === null ||
              recoveryInProgress ||
              pendingQueueItemId !== null
            }
            startLabel={
              workState?.activeStepId ? "Switch and start" : "Start working"
            }
            activeQueueItemId={workState?.activeQueueItemId ?? null}
            onShowTimer={() => {
              activeHeading.current?.focus();
              activeHeading.current?.scrollIntoView({ block: "center" });
            }}
          />
        )}
        {queue === null &&
        (workState === null || connectivity !== "connected") ? (
          <p role="status">
            Waiting for a live connection to your day. Starting work becomes
            available when the connection is confirmed.
          </p>
        ) : null}
        {schoolworkProgress === null ? null : (
          <>
            <UnderstandingChecks
              connectivity={connectivity}
              progress={schoolworkProgress}
              onCommand={changeSchoolwork}
            />
            <AssignmentProgress
              role="student"
              connectivity={connectivity}
              progress={schoolworkProgress}
            />
          </>
        )}
        <DayCloseoutRegion
          snapshot={snapshot}
          role="student"
          sessionClient={sessionClient}
          connectivity={connectivity}
          overrides={parentOverrides}
          onSafeAlternative={useSafeAlternative}
        />
      </main>
    </div>
  );
}

function LoadedSurface({
  path,
  snapshot,
  sessionClient,
  resourceLauncher,
  connectivity,
  policyDeliveryState,
  onRetryPolicyDelivery,
  onConnectivityConflict,
  theme,
  onThemeChange,
}: {
  path: string;
  snapshot: HouseholdSnapshot;
  sessionClient: SessionClient;
  resourceLauncher: ResourceLauncher;
  connectivity: FocusBlockConnectivity;
  policyDeliveryState: PolicyDeliveryLoadState;
  onRetryPolicyDelivery: () => void;
  onConnectivityConflict: () => void;
  theme: ThemeId;
  onThemeChange: (theme: ThemeId) => void;
}) {
  const requiredRole = expectedRole(path);
  if (snapshot.role !== requiredRole) {
    return (
      <main className="centered-state" id="main-content">
        <p className="eyebrow">Permission needed</p>
        <h1>
          {requiredRole === "parent"
            ? "Parent access required"
            : "Student access required"}
        </h1>
        <p>This session cannot open that surface.</p>
        <a href={snapshot.role === "parent" ? "/parent" : "/student"}>
          Return to your dashboard
        </a>
      </main>
    );
  }
  if (path === "/parent") {
    return (
      <ParentDashboard
        snapshot={snapshot}
        sessionClient={sessionClient}
        connectivity={connectivity}
        policyDeliveryState={policyDeliveryState}
        onRetryPolicyDelivery={onRetryPolicyDelivery}
        onConnectivityConflict={onConnectivityConflict}
        theme={theme}
        onThemeChange={onThemeChange}
      />
    );
  }
  return (
    <StudentSurface
      snapshot={snapshot}
      workspace={path === "/workspace"}
      sessionClient={sessionClient}
      resourceLauncher={resourceLauncher}
      connectivity={connectivity}
      onConnectivityConflict={onConnectivityConflict}
      theme={theme}
      onThemeChange={onThemeChange}
    />
  );
}

export function App({
  initialPath = window.location.pathname,
  sessionClient = browserSessionClient,
  resourceLauncher = browserResourceLauncher,
}: AppProps) {
  const [path, setPath] = useState(initialPath);
  const [theme, setTheme] = useState<ThemeId>(() =>
    readStoredTheme(window.localStorage),
  );
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [connectivity, setConnectivity] =
    useState<FocusBlockConnectivity>("reconnecting");
  const [policyDeliveryState, setPolicyDeliveryState] =
    useState<PolicyDeliveryLoadState>({ status: "loading" });
  const [policyDeliveryAttempt, setPolicyDeliveryAttempt] = useState(0);
  const policyStudentId =
    state.status === "loaded" && state.snapshot.role === "parent"
      ? state.snapshot.student.id
      : null;

  useEffect(() => {
    applyTheme(theme, document.documentElement);
    storeTheme(theme, window.localStorage);
  }, [theme]);

  useEffect(() => {
    const syncTheme = (event: StorageEvent) => {
      if (event.key === themeStorageKey) {
        setTheme(readStoredTheme(window.localStorage));
      }
    };
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  useEffect(() => {
    const offline = () => setConnectivity("offline");
    const online = () => setConnectivity("reconnecting");
    window.addEventListener("offline", offline);
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", online);
    };
  }, []);

  useEffect(() => {
    if (path === "/login") return;
    let current = true;
    setState({ status: "loading" });
    setRefreshError(null);
    void sessionClient.load().then(
      (snapshot) => {
        if (current) {
          setConnectivity(
            typeof navigator !== "undefined" && navigator.onLine === false
              ? "offline"
              : "reconnecting",
          );
          setState({ status: "loaded", snapshot });
        }
      },
      (error: unknown) => {
        if (current) {
          setState({
            status: "error",
            kind:
              error instanceof AuthenticationRequiredError
                ? "authentication-required"
                : "unavailable",
            message:
              error instanceof Error
                ? error.message
                : "Today’s plan could not be loaded.",
          });
        }
      },
    );
    return () => {
      current = false;
    };
  }, [attempt, path, sessionClient]);

  useEffect(() => {
    if (policyStudentId === null) {
      setPolicyDeliveryState({ status: "loading" });
      return;
    }
    const loadStatuses = sessionClient.loadPolicyDeliveryStatuses;
    if (loadStatuses === undefined) {
      setPolicyDeliveryState({ status: "error" });
      return;
    }
    let current = true;
    const refreshStatuses = () => {
      setPolicyDeliveryState({ status: "loading" });
      void loadStatuses(policyStudentId).then(
        (statuses) => {
          if (current)
            setPolicyDeliveryState({ status: "loaded", surfaces: statuses });
        },
        () => {
          if (current) setPolicyDeliveryState({ status: "error" });
        },
      );
    };
    refreshStatuses();
    const timer = window.setInterval(refreshStatuses, 15_000);
    return () => {
      current = false;
      window.clearInterval(timer);
    };
  }, [policyDeliveryAttempt, policyStudentId, sessionClient]);

  useEffect(() => {
    if (state.status !== "loaded") return;
    const subscribe = sessionClient.subscribeToDailyQueueChanges;
    let current = true;
    let refreshing = false;
    let refreshQueued = false;
    let latestAcknowledgedSnapshot: HouseholdSnapshot | null = null;
    const surfaceKind: SurfaceKind =
      path === "/parent"
        ? "parent-dashboard"
        : path === "/workspace"
          ? "managed-workspace"
          : "student-console";
    const acceptSnapshot = async (snapshot: HouseholdSnapshot) => {
      if (!current) return;
      setRefreshError(null);
      setState({ status: "loaded", snapshot });
      const acknowledge = sessionClient.acknowledgeSurface;
      if (acknowledge === undefined) {
        setConnectivity(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "reconnecting",
        );
        setRefreshError(
          "The latest state is visible, but this surface cannot confirm receipt.",
        );
        return;
      }
      setConnectivity("pending");
      try {
        await acknowledge(surfaceKind, snapshot);
        if (current) {
          latestAcknowledgedSnapshot = snapshot;
          setConnectivity("connected");
        }
      } catch {
        if (!current) return;
        setConnectivity(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "reconnecting",
        );
        setRefreshError(
          "The latest state is visible but this surface could not acknowledge it yet.",
        );
      }
    };
    const refresh = async () => {
      if (refreshing) {
        refreshQueued = true;
        return;
      }
      refreshing = true;
      do {
        refreshQueued = false;
        try {
          const snapshot = await sessionClient.load();
          await acceptSnapshot(snapshot);
        } catch {
          if (current) {
            setConnectivity(
              typeof navigator !== "undefined" && navigator.onLine === false
                ? "offline"
                : "reconnecting",
            );
            setRefreshError(
              "Live Daily Queue updates could not be refreshed. The current plan is still available.",
            );
          }
        }
      } while (current && refreshQueued);
      refreshing = false;
    };
    const unsubscribe =
      subscribe?.(
        state.snapshot.student.id,
        () => {
          void refresh();
        },
        setConnectivity,
        surfaceKind,
        state.snapshot.student.eventSequence,
        (snapshot) => {
          void acceptSnapshot(snapshot);
        },
      ) ?? (() => undefined);
    if (subscribe === undefined) {
      setConnectivity(
        typeof navigator !== "undefined" && navigator.onLine === false
          ? "offline"
          : "reconnecting",
      );
      setRefreshError(
        "The saved state is readable, but live confirmation is unavailable.",
      );
    }
    const heartbeat = window.setInterval(() => {
      const snapshot = latestAcknowledgedSnapshot;
      const acknowledge = sessionClient.acknowledgeSurface;
      if (snapshot === null || acknowledge === undefined) return;
      void acknowledge(surfaceKind, snapshot).catch(() => {
        if (!current) return;
        setConnectivity(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "reconnecting",
        );
        setRefreshError(
          "This surface could not refresh its live acknowledgment. The last confirmed state remains visible.",
        );
      });
    }, 15_000);
    const refreshAfterVisibility = () => {
      if (document.visibilityState !== "visible") return;
      setConnectivity("reconnecting");
      void refresh();
    };
    document.addEventListener("visibilitychange", refreshAfterVisibility);
    return () => {
      current = false;
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", refreshAfterVisibility);
      unsubscribe();
    };
  }, [path, sessionClient, state.status]);

  if (path === "/login") {
    return (
      <SignIn
        sessionClient={sessionClient}
        onAuthenticated={(role) => {
          const destination = role === "parent" ? "/parent" : "/student";
          window.history.replaceState(null, "", destination);
          setState({ status: "loading" });
          setPath(destination);
        }}
      />
    );
  }
  if (state.status === "loading") {
    return (
      <main className="centered-state" id="main-content">
        <p role="status" aria-live="polite">
          Loading today’s plan…
        </p>
      </main>
    );
  }
  if (state.status === "error") {
    return (
      <main className="centered-state" id="main-content">
        <p className="eyebrow">Plan unavailable</p>
        <h1>We couldn’t load today’s plan</h1>
        <p role="alert">{state.message}</p>
        {state.kind === "authentication-required" ? (
          <a className="button-link" href="/login">
            Sign in
          </a>
        ) : (
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </button>
        )}
      </main>
    );
  }
  return (
    <div>
      {refreshError === null ? null : (
        <div className="live-refresh-error" role="alert">
          <span>{refreshError}</span>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry live sync
          </button>
        </div>
      )}
      <LoadedSurface
        path={path}
        snapshot={state.snapshot}
        sessionClient={sessionClient}
        resourceLauncher={resourceLauncher}
        connectivity={connectivity}
        policyDeliveryState={policyDeliveryState}
        onRetryPolicyDelivery={() =>
          setPolicyDeliveryAttempt((value) => value + 1)
        }
        onConnectivityConflict={() => setConnectivity("conflict")}
        theme={theme}
        onThemeChange={setTheme}
      />
    </div>
  );
}
